// src/AdminPage.js — Админка: товары + подписчики
import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import EditProfileModal from './components/EditProfileModal';
import AuditLogTab from './components/admin/AuditLogTab';
import FunnelTab from './components/admin/FunnelTab';

// Подключаем Montserrat через Google Fonts
if (typeof document !== 'undefined' && !document.getElementById('montserrat-font')) {
  const link = document.createElement('link');
  link.id = 'montserrat-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const API = '';

function AdminPage() {
  const { telegramId } = useUser();
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [loggedIn, setLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [tab, setTab] = useState('dashboard');

  // Products state
  const [catalogs, setCatalogs] = useState([]);
  const [activeCatalog, setActiveCatalog] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null); // {catalogId, index, item}
  const [editingProduct, setEditingProduct] = useState({ price: '', discount: '' });
  const [saving, setSaving] = useState(false);
  const [upsellItems, setUpsellItems] = useState([]); // [{sku, name, price, catalog}]
  const [upsellSaving, setUpsellSaving] = useState(null);
  const [upsellClearing, setUpsellClearing] = useState(false);
  const [promoGiftSkus, setPromoGiftSkus] = useState([]);
  const [thresholdGiftSkus, setThresholdGiftSkus] = useState([]);
  const [giftRules, setGiftRules] = useState({ promoRules: [], thresholdRules: [] });
  const [giftRulesLoading, setGiftRulesLoading] = useState(false);
  const [giftRuleSaving, setGiftRuleSaving] = useState(null);
  const [giftRuleForm, setGiftRuleForm] = useState({
    id: '',
    type: 'promo',
    code: '',
    threshold: '',
    sku: '',
    enabled: true,
  });

  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [cancelingAutoRenew, setCancelingAutoRenew] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsFilter, setSubsFilter] = useState('all');
  const [subsSearch, setSubsSearch] = useState('');
  const [grantingGift, setGrantingGift] = useState(null); // telegram_id пока идёт запрос
  const [resettingSub, setResettingSub] = useState(null); // telegram_id пока идёт сброс подписки
  const [settingTariff, setSettingTariff] = useState(null); // telegram_id пока идёт смена тарифа
  const [extendingDays, setExtendingDays] = useState({}); // {telegram_id: '5'}
  const [extending, setExtending] = useState(null); // telegram_id пока идёт продление
  const [shcAmount, setShcAmount] = useState('');
  const [addingShc, setAddingShc] = useState(null); // telegram_id пока идёт начисление
  // inline смена тарифа с датой: { telegram_id, tariff, end_date }
  const [subEdit, setSubEdit] = useState(null);
  const [dashStats, setDashStats] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [giftOrders, setGiftOrders] = useState(null);
  const [giftOrdersLoading, setGiftOrdersLoading] = useState(false);
  const [giftOrdersSearch, setGiftOrdersSearch] = useState('');

  // Referrals state
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralSearch, setReferralSearch] = useState('');

  // UI state
  const [catalogMenuOpen, setCatalogMenuOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toasts, setToasts] = useState([]);

  // Banners state
  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(null);

  // Pricing state
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);

  // Stores state
  const [storesState, setStoresState] = useState(null);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesSaving, setStoresSaving] = useState(null);

  // Add user state
  const [addName, setAddName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addTariff, setAddTariff] = useState('290');
  const [addGiftRolls, setAddGiftRolls] = useState(0);
  const [addGiftSets, setAddGiftSets] = useState(0);
  const [addEndDate, setAddEndDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState(null);

  // Add product state
  const [newCatId, setNewCatId] = useState('rolls');
  const [newName, setNewName] = useState('');
  const [newSku, setNewSku] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newAddToSub, setNewAddToSub] = useState(false);
  const [newSubSku, setNewSubSku] = useState('');
  const [newProductLoading, setNewProductLoading] = useState(false);
  const [newProductResult, setNewProductResult] = useState(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const loadStores = useCallback(async () => {
    setStoresLoading(true);
    try {
      const res = await fetch('/api/admin/stores', { headers: headers() });
      const data = await res.json();
      if (data.success) setStoresState(data);
    } catch (_) {}
    setStoresLoading(false);
  }, [headers]);

  const toggleStore = async (pointId, current) => {
    setStoresSaving(String(pointId));
    try {
      const res = await fetch('/api/admin/stores', {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ pointId: String(pointId), enabled: !current }),
      });
      const data = await res.json();
      if (data.success) { await loadStores(); showToast(data.enabled ? 'Точка включена' : 'Точка выключена'); }
      else showToast(data.error || 'Ошибка', 'error');
    } catch (_) { showToast('Ошибка сети', 'error'); }
    setStoresSaving(null);
  };

  // Проверка токена при загрузке
  useEffect(() => {
    if (token) {
      fetch(`${API}/api/admin/products`, { headers: headers() })
        .then(r => { if (r.ok) setLoggedIn(true); else { setToken(''); localStorage.removeItem('admin_token'); } })
        .catch(() => { setToken(''); localStorage.removeItem('admin_token'); });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Логин
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.success) {
        setToken(data.token);
        localStorage.setItem('admin_token', data.token);
        setLoggedIn(true);
      } else {
        setLoginError(data.error || 'Неверный пароль');
      }
    } catch {
      setLoginError('Ошибка соединения');
    }
  };

  // Загрузка товаров
  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/products`, { headers: headers() });
      const data = await res.json();
      if (data.success) {
        setCatalogs(data.catalogs);
        if (!activeCatalog && data.catalogs.length > 0) {
          setActiveCatalog(data.catalogs[0].id);
        }
      }
    } catch (err) {
      console.error('loadProducts error:', err);
    }
    setProductsLoading(false);
  }, [headers, activeCatalog]);

  // upsellSkus — производный массив для кнопок в табе «Товары»
  const upsellSkus = upsellItems.map(i => i.sku);

  const loadUpsell = useCallback(async () => {
    try {
      const res = await fetch('/api/upsell-items');
      const data = await res.json();
      if (data.success) setUpsellItems(data.items);
    } catch (_) {}
  }, []);

  const toggleUpsell = async (sku, inUpsell) => {
    setUpsellSaving(String(sku));
    try {
      const res = await fetch('/api/admin/upsell-toggle', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ sku: String(sku), action: inUpsell ? 'remove' : 'add' }),
      });
      const data = await res.json();
      if (data.success) {
        setUpsellItems(prev =>
          inUpsell ? prev.filter(i => i.sku !== String(sku)) : prev // при add — перезагрузим
        );
        if (!inUpsell) await loadUpsell(); // нужны полные данные нового товара
        showToast(inUpsell ? 'Убрано из допродаж' : 'Добавлено в допродажи');
      } else {
        showToast(data.error || 'Ошибка', 'error');
      }
    } catch (_) {
      showToast('Ошибка сети', 'error');
    }
    setUpsellSaving(null);
  };

  const clearAllUpsell = async () => {
    if (!window.confirm('Очистить весь список допродаж?')) return;
    setUpsellClearing(true);
    try {
      const res = await fetch('/api/admin/upsell-clear', {
        method: 'POST',
        headers: headers(),
      });
      const data = await res.json();
      if (data.success) { setUpsellItems([]); showToast('Список очищен'); }
      else showToast(data.error || 'Ошибка', 'error');
    } catch (_) { showToast('Ошибка сети', 'error'); }
    setUpsellClearing(false);
  };

  // Загрузка правил подарков
  const loadGifts = useCallback(async () => {
    setGiftRulesLoading(true);
    try {
      const res = await fetch('/api/admin/gift-rules', { headers: headers() });
      const data = await res.json();
      if (data.success) {
        setGiftRules({
          promoRules: data.promoRules || [],
          thresholdRules: data.thresholdRules || [],
        });
        setPromoGiftSkus(data.promoSkus || []);
        setThresholdGiftSkus(data.thresholdSkus || []);
      }
    } catch (_) {}
    setGiftRulesLoading(false);
  }, [headers]);

  const resetGiftRuleForm = () => {
    const type = tab === 'threshold-gifts' ? 'threshold' : 'promo';
    setGiftRuleForm({ id: '', type, code: '', threshold: '', sku: '', enabled: true });
  };

  const saveGiftRule = async () => {
    const isEdit = !!giftRuleForm.id;
    const type = tab === 'threshold-gifts' ? 'threshold' : 'promo';
    if (type === 'promo' && !giftRuleForm.code.trim()) {
      showToast('Введите промокод', 'error');
      return;
    }
    if (!giftRuleForm.sku) {
      showToast('Выберите подарок', 'error');
      return;
    }
    if (!Number(giftRuleForm.threshold)) {
      showToast('Введите порог суммы', 'error');
      return;
    }

    setGiftRuleSaving('form');
    try {
      const res = await fetch('/api/admin/gift-rules', {
        method: isEdit ? 'PUT' : 'POST',
        headers: headers(),
        body: JSON.stringify({
          id: giftRuleForm.id || undefined,
          type,
          code: type === 'promo' ? giftRuleForm.code.trim() : undefined,
          threshold: Number(giftRuleForm.threshold),
          sku: String(giftRuleForm.sku),
          enabled: giftRuleForm.enabled !== false,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setGiftRules({ promoRules: data.promoRules || [], thresholdRules: data.thresholdRules || [] });
        setPromoGiftSkus(data.promoSkus || []);
        setThresholdGiftSkus(data.thresholdSkus || []);
        resetGiftRuleForm();
        showToast(isEdit ? 'Правило сохранено' : 'Правило добавлено');
      } else {
        showToast(data.error || 'Ошибка', 'error');
      }
    } catch (_) {
      showToast('Ошибка сети', 'error');
    }
    setGiftRuleSaving(null);
  };

  const editGiftRule = (rule, type) => {
    setGiftRuleForm({
      id: rule.id,
      type,
      code: rule.code || '',
      threshold: String(rule.threshold || ''),
      sku: String(rule.sku || ''),
      enabled: rule.enabled !== false,
    });
  };

  const toggleGiftRule = async (rule, type) => {
    setGiftRuleSaving(rule.id);
    try {
      const res = await fetch('/api/admin/gift-rules', {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ ...rule, type, enabled: rule.enabled === false }),
      });
      const data = await res.json();
      if (data.success) {
        setGiftRules({ promoRules: data.promoRules || [], thresholdRules: data.thresholdRules || [] });
        setPromoGiftSkus(data.promoSkus || []);
        setThresholdGiftSkus(data.thresholdSkus || []);
        showToast(rule.enabled === false ? 'Акция включена' : 'Акция выключена');
      } else {
        showToast(data.error || 'Ошибка', 'error');
      }
    } catch (_) {
      showToast('Ошибка сети', 'error');
    }
    setGiftRuleSaving(null);
  };

  const deleteGiftRule = async (rule) => {
    if (!window.confirm('Удалить правило подарка?')) return;
    setGiftRuleSaving(rule.id);
    try {
      const res = await fetch('/api/admin/gift-rules', {
        method: 'DELETE',
        headers: headers(),
        body: JSON.stringify({ id: rule.id }),
      });
      const data = await res.json();
      if (data.success) {
        setGiftRules({ promoRules: data.promoRules || [], thresholdRules: data.thresholdRules || [] });
        setPromoGiftSkus(data.promoSkus || []);
        setThresholdGiftSkus(data.thresholdSkus || []);
        if (giftRuleForm.id === rule.id) resetGiftRuleForm();
        showToast('Правило удалено');
      } else {
        showToast(data.error || 'Ошибка', 'error');
      }
    } catch (_) {
      showToast('Ошибка сети', 'error');
    }
    setGiftRuleSaving(null);
  };

  const openGiftRuleForProduct = (sku, type = 'promo') => {
    const normalizedType = type === 'threshold' ? 'threshold' : 'promo';
    setGiftRuleForm(prev => ({ ...prev, id: '', type: normalizedType, code: '', sku: String(sku), enabled: true }));
    setSelectedProduct(null);
    setTab(normalizedType === 'threshold' ? 'threshold-gifts' : 'promo-gifts');
  };

  // Загрузка подписчиков
  const loadSubscribers = useCallback(async () => {
    setSubsLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/subscribers`, { headers: headers() });
      const data = await res.json();
      if (data.success) {
        setSubscribers(data.subscribers);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('loadSubscribers error:', err);
    }
    setSubsLoading(false);
  }, [headers]);

  useEffect(() => {
    if (loggedIn && tab === 'dashboard') { loadGifts(); loadUpsell(); }
    if (loggedIn && tab === 'products' && catalogs.length === 0) { loadProducts(); loadUpsell(); loadGifts(); }
    if (loggedIn && (tab === 'promo-gifts' || tab === 'threshold-gifts')) {
      loadGifts();
      if (catalogs.length === 0) loadProducts();
      if (!giftRuleForm.id) {
        setGiftRuleForm(prev => ({ ...prev, type: tab === 'threshold-gifts' ? 'threshold' : 'promo' }));
      }
    }
    if (loggedIn && tab === 'upsell') loadUpsell();
    if (loggedIn && tab === 'subscribers' && subscribers.length === 0) loadSubscribers();
    if (loggedIn && tab === 'banners' && banners.length === 0) loadBanners();
    if (loggedIn && tab === 'pricing' && !pricing) loadPricing();
    if (loggedIn && tab === 'orders') { loadGiftOrders(); loadStats(); }
    if (loggedIn && tab === 'referrals') loadReferralData();
    if (loggedIn && tab === 'stores') loadStores();
  }, [loggedIn, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedUser) {
      const updated = subscribers.find(s => s.telegram_id === selectedUser.telegram_id);
      if (updated) setSelectedUser(updated);
    }
  }, [subscribers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedProduct) return;
    const cat = catalogs.find(c => c.id === selectedProduct.catalogId);
    if (!cat) return;
    const updated = cat.items[selectedProduct.index];
    if (updated) setSelectedProduct(prev => ({ ...prev, item: updated }));
  }, [catalogs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Сохранение товара
  const saveItem = async (catalogId, index, updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/products`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ catalogId, itemIndex: index, ...updates }),
      });
      const data = await res.json();
      if (data.success) {
        setCatalogs(prev => prev.map(cat => {
          if (cat.id !== catalogId) return cat;
          return {
            ...cat,
            items: cat.items.map((item, i) => i === index ? { ...item, ...updates } : item),
          };
        }));
        return true;
      }
    } catch (err) {
      console.error('saveItem error:', err);
    } finally {
      setSaving(false);
    }
    return false;
  };

  // Toggle enabled
  const toggleEnabled = (catalogId, index, currentEnabled) => {
    saveItem(catalogId, index, { enabled: !currentEnabled });
  };

  // Выдать подарок
  const grantGift = async (telegramId, type) => {
    setGrantingGift(telegramId + '+' + type);
    try {
      const res = await fetch(`${API}/api/admin/grant-gift`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ telegram_id: telegramId, type }),
      });
      const data = await res.json();
      if (data.success) { loadSubscribers(); showToast(`✓ Подарок выдан: ${type}`); }
      else showToast(data.error || 'Ошибка', 'error');
    } catch (err) {
      console.error('grantGift error:', err);
      showToast('Ошибка соединения', 'error');
    }
    setGrantingGift(null);
  };

  // Отметить подарок полученным
  const claimGift = async (telegramId, type) => {
    setGrantingGift(telegramId + '-' + type);
    try {
      const res = await fetch(`${API}/api/admin/claim-gift`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ telegram_id: telegramId, type }),
      });
      const data = await res.json();
      if (data.success) { loadSubscribers(); showToast(`✓ Отмечено: ${type}`); }
      else showToast(data.error || 'Ошибка', 'error');
    } catch (err) {
      console.error('claimGift error:', err);
      showToast('Ошибка соединения', 'error');
    }
    setGrantingGift(null);
  };

  // Отмена будущих списаний без сброса оплаченного доступа.
  const cancelAutoRenew = async (user) => {
    if (cancelingAutoRenew) return;
    if (!window.confirm('Отключить автопродление у этого пользователя? Оплаченный срок подписки сохранится.')) return;
    setCancelingAutoRenew(user.telegram_id);
    try {
      const res = await fetch(`${API}/api/admin/cancel-subscription`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ telegram_id: user.telegram_id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Не удалось отключить автопродление');
      // Обновляем открытую карточку сразу, без зависимости от повторной загрузки списка.
      setSubscribers(prev => prev.map(item => item.telegram_id === user.telegram_id
        ? { ...item, auto_renew_disabled: true, has_payment_method: false }
        : item));
      showToast(data.message);
    } catch (error) {
      showToast(error.message || 'Ошибка соединения', 'error');
    } finally {
      setCancelingAutoRenew(null);
    }
  };

  // Полный сброс подписки остаётся отдельным действием.
  const resetSubscription = async (telegramId, name) => {
    if (!window.confirm(`Сбросить подписку пользователя "${name || telegramId}"?\nОн увидит экран оплаты при следующем входе.`)) return;
    setResettingSub(telegramId);
    try {
      const res = await fetch(`${API}/api/admin/reset-subscription`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ telegram_id: telegramId }),
      });
      const data = await res.json();
      if (data.success) loadSubscribers();
    } catch (err) {
      console.error('resetSubscription error:', err);
    }
    setResettingSub(null);
  };

  // Смена тарифа + дата окончания
  const applySubscription = async () => {
    if (!subEdit) return;
    setSettingTariff(subEdit.telegram_id);
    try {
      const res = await fetch(`${API}/api/admin/set-subscription`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ telegram_id: subEdit.telegram_id, tariff: subEdit.tariff, end_date: subEdit.end_date }),
      });
      const data = await res.json();
      if (data.success) { loadSubscribers(); setSubEdit(null); }
    } catch (err) {
      console.error('applySubscription error:', err);
    }
    setSettingTariff(null);
  };

  const defaultEndDate = () => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  };

  // Продление подписки на N дней
  const extendSub = async (telegramId) => {
    const days = Number(extendingDays[telegramId]);
    if (!days || days < 1) return;
    setExtending(telegramId);
    try {
      const res = await fetch(`${API}/api/admin/extend-subscription`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ telegram_id: telegramId, days }),
      });
      const data = await res.json();
      if (data.success) { loadSubscribers(); setExtendingDays(prev => ({ ...prev, [telegramId]: '' })); }
    } catch (err) { console.error('extendSub error:', err); }
    setExtending(null);
  };

  // Ручное начисление SHC пользователю
  const addShc = async (telegramId) => {
    const amount = Number(shcAmount);
    if (!Number.isInteger(amount) || amount < 1 || amount > 1000000) {
      showToast('Введите целое число от 1 до 1 000 000', 'error');
      return;
    }
    if (!window.confirm(`Начислить пользователю ${amount} SHC?`)) return;

    setAddingShc(telegramId);
    try {
      const res = await fetch(`${API}/api/admin/add-shc`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ telegram_id: telegramId, amount }),
      });
      const data = await res.json();
      if (data.success) {
        setShcAmount('');
        await loadSubscribers();
        showToast(`✓ Начислено ${amount} SHC`);
      } else {
        showToast(data.error || 'Не удалось начислить SHC', 'error');
      }
    } catch (err) {
      console.error('addShc error:', err);
      showToast('Ошибка соединения', 'error');
    }
    setAddingShc(null);
  };

  const showToast = useCallback((text, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, text, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }, []);

  // Сохранить заметку
  const saveNotes = async (telegramId, notes) => {
    try {
      await fetch(`${API}/api/admin/user-notes`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ telegram_id: telegramId, notes }),
      });
    } catch (err) { console.error('saveNotes error:', err); }
  };

  // Загрузка статистики
  const loadStats = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/stats`, { headers: headers() });
      const data = await res.json();
      if (data.success) setDashStats(data.stats);
    } catch (_) {}
    setDashLoading(false);
  }, [headers]);

  // Загрузка заказов подарков
  const loadGiftOrders = useCallback(async () => {
    setGiftOrdersLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/gift-orders`, { headers: headers() });
      const data = await res.json();
      if (data.success) setGiftOrders(data.orders);
    } catch (_) {}
    setGiftOrdersLoading(false);
  }, [headers]);

  const loadReferralData = useCallback(async () => {
    setReferralLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/referrals`, { headers: headers() });
      const data = await res.json();
      if (data.success) setReferralData(data);
    } catch (_) {}
    setReferralLoading(false);
  }, [headers]);

  // Загрузка цен
  const loadPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/pricing`, { headers: headers() });
      const data = await res.json();
      if (data.success) setPricing(data.pricing);
    } catch (err) {
      console.error('loadPricing error:', err);
    }
    setPricingLoading(false);
  }, [headers]);

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      await fetch(`${API}/api/admin/pricing`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ pricing }),
      });
    } catch (err) {
      console.error('savePricing error:', err);
    }
    setPricingSaving(false);
  };

  // Коэффициенты скидок (из базового тарифа 290: 750/290 и 1200/290)
  const MONTH_RATIO_3 = 750 / 290;  // ~2.586 → −13.8% от полной цены
  const MONTH_RATIO_5 = 1200 / 290; // ~4.138 → −17.2% от полной цены

  const updatePrice = (tarif, field, value) => {
    const numVal = Number(value) || 0;
    setPricing(prev => {
      const updated = { ...prev };
      if (field === 'price') {
        const months = { ...updated[tarif].months, 1: numVal };
        if (tarif !== '9990') {
          months[3] = Math.round(numVal * MONTH_RATIO_3 / 10) * 10;
          months[5] = Math.round(numVal * MONTH_RATIO_5 / 10) * 10;
        }
        updated[tarif] = { ...updated[tarif], price: numVal, months };
      } else {
        updated[tarif] = { ...updated[tarif], months: { ...updated[tarif].months, [field]: numVal } };
      }
      return updated;
    });
  };

  // Загрузка баннеров
  const loadBanners = useCallback(async () => {
    setBannersLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/banners`, { headers: headers() });
      const data = await res.json();
      if (data.success) setBanners(data.banners);
    } catch (err) {
      console.error('loadBanners error:', err);
    }
    setBannersLoading(false);
  }, [headers]);

  const cropToAspect = (file, ratio) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const targetW = img.width;
      const canvas = document.createElement('canvas');
      canvas.width = Math.min(targetW, 1200);
      canvas.height = Math.round(canvas.width / ratio);
      const ctx = canvas.getContext('2d');
      // Центрируем обрезку по вертикали
      const srcH = Math.round(img.width / ratio);
      const srcY = Math.max(0, Math.round((img.height - srcH) / 2));
      ctx.drawImage(img, 0, srcY, img.width, srcH, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.src = URL.createObjectURL(file);
  });

  const uploadBanner = async (slot, file) => {
    setBannerUploading(slot);
    try {
      const imageData = await cropToAspect(file, 8 / 3);
      const res = await fetch(`${API}/api/admin/banners`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ slot, imageData }),
      });
      const data = await res.json();
      if (data.success) loadBanners();
    } catch (err) {
      console.error('uploadBanner error:', err);
    }
    setBannerUploading(null);
  };

  const addBannerSlot = async () => {
    const newId = banners.length > 0 ? Math.max(...banners.map(b => b.id)) + 1 : 1;
    if (newId > 7) return;
    const updated = [...banners, { id: newId, placeholder: true, color: '#f5f5f5' }];
    setBanners(updated);
    try {
      await fetch(`${API}/api/admin/banners`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ action: 'set-all', banners: updated }),
      });
    } catch {}
  };

  const removeBannerSlot = async () => {
    if (banners.length <= 1) return;
    const last = banners[banners.length - 1];
    // Удаляем файл если есть
    if (last.image) {
      try {
        await fetch(`${API}/api/admin/banners`, {
          method: 'DELETE',
          headers: headers(),
          body: JSON.stringify({ slot: last.id }),
        });
      } catch {}
    }
    const updated = banners.slice(0, -1);
    setBanners(updated);
    try {
      await fetch(`${API}/api/admin/banners`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ action: 'set-all', banners: updated }),
      });
    } catch {}
  };

  const deleteBanner = async (slot) => {
    setBannerUploading(slot);
    try {
      const res = await fetch(`${API}/api/admin/banners`, {
        method: 'DELETE',
        headers: headers(),
        body: JSON.stringify({ slot }),
      });
      const data = await res.json();
      if (data.success) loadBanners();
    } catch (err) {
      console.error('deleteBanner error:', err);
    }
    setBannerUploading(null);
  };

  const handleNewProductImage = async (file) => {
    const imageData = await cropToAspect(file, 1);
    setNewImagePreview(imageData);
  };

  const isGiftCat = (catId) => catId === 'gift-roll' || catId === 'gift-set';

  const addProduct = async (e) => {
    e.preventDefault();
    setNewProductLoading(true);
    setNewProductResult(null);
    try {
      const res = await fetch(`${API}/api/admin/add-product`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          categoryId: newCatId,
          name: newName.trim(),
          sku: newSku.trim(),
          ...(!isGiftCat(newCatId) ? { price: Number(newPrice) } : {}),
          description: newDesc.trim() || undefined,
          imageData: newImagePreview || undefined,
          ...(!isGiftCat(newCatId) ? { addToSub: newAddToSub, subSku: newSubSku.trim() || undefined } : {}),
        }),
      });
      const data = await res.json();
      setNewProductResult(data);
      if (data.success) {
        setNewName(''); setNewSku(''); setNewPrice('');
        setNewDesc(''); setNewImagePreview(null);
        setNewAddToSub(false); setNewSubSku('');
      }
    } catch {
      setNewProductResult({ success: false, error: 'Ошибка соединения' });
    }
    setNewProductLoading(false);
  };

  const logout = () => {
    setToken('');
    setLoggedIn(false);
    localStorage.removeItem('admin_token');
  };

  const addUser = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddResult(null);
    try {
      const res = await fetch(`${API}/api/admin/add-user-manual`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          name: addName.trim() || undefined,
          phone: addPhone.trim(),
          tariff: addTariff,
          end_date: addEndDate,
          gift_rolls: Number(addGiftRolls) || 0,
          gift_sets: Number(addGiftSets) || 0,
        }),
      });
      const data = await res.json();
      setAddResult(data);
      if (data.success) {
        setAddPhone('');
        setAddName('');
        setAddTariff('290');
        setAddGiftRolls(0);
        setAddGiftSets(0);
        const d = new Date(); d.setDate(d.getDate() + 30);
        setAddEndDate(d.toISOString().split('T')[0]);
      }
    } catch {
      setAddResult({ success: false, error: 'Ошибка соединения' });
    }
    setAddLoading(false);
  };

  // ─── Login screen ────────────────────────────────────
  if (!loggedIn) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <h2 style={styles.title}>Админка Суши-Хаус 39</h2>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Пароль"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={styles.input}
              autoFocus
            />
            <button type="submit" style={styles.btnPrimary}>Войти</button>
          </form>
          {loginError && <p style={styles.error}>{loginError}</p>}
          <button
            onClick={() => { window.location.href = telegramId ? `/discount-shop?telegram_id=${telegramId}` : '/discount-shop'; }}
            style={{ ...styles.btnSmall, marginTop: 16, width: '100%' }}
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  // ─── Main admin panel ────────────────────────────────
  const currentCatalog = catalogs.find(c => c.id === activeCatalog);
  const giftProductOptions = catalogs.flatMap(c =>
    (c.items || [])
      .filter(item => item.sku && item.enabled !== false)
      .map(item => ({
        sku: String(item.sku),
        name: item.name,
        catalog: c.name,
      }))
  );
  const filteredSubs = subscribers.filter(s => {
    if (subsFilter !== 'all' && s.tariff !== subsFilter) return false;
    if (subsSearch) {
      const q = subsSearch.toLowerCase();
      return (s.name || '').toLowerCase().includes(q)
        || (s.phone || '').includes(q)
        || (s.telegram_id || '').includes(q);
    }
    return true;
  });

  const TAB_LABELS = {
    dashboard:     '▦ Дашборд',
    products:      '📦 Товары',
    'promo-gifts': '🎟 Промокоды',
    'threshold-gifts': '🎁 Подарок за чек',
    upsell:        '🛒 Допродажи',
    subscribers:   '👥 Люди',
    orders:        '📋 Заказы и статистика',
    banners:       '🖼 Баннеры',
    pricing:       '💰 Цены',
    referrals:     '🔗 Рефералы',
    add:           '👤 Добавить пользователя',
    'add-product': '➕ Добавить товар',
    stores:        '📍 Точки самовывоза',
    audit:         '📜 Журнал',
    funnels:       '📊 Воронки',
  };

  return (
    <div style={{ ...styles.container, ...(['audit', 'funnels'].includes(tab) ? { maxWidth: 1100 } : {}) }}>
      <style>{`
        .adm-user-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
          margin-top: 10px;
          width: 100%;
          box-sizing: border-box;
        }
        .adm-user-card {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          background: #202024;
          border: none;
          border-radius: 12px;
          padding: 10px;
          cursor: pointer;
          text-align: left;
          box-shadow: 4px 4px 10px #111113, -2px -2px 8px #2c2c30;
          width: 100%;
          min-height: 0;
          transition: transform 0.12s;
          box-sizing: border-box;
          overflow: hidden;
          font-family: "Montserrat", "Segoe UI", Arial, sans-serif;
        }
        .adm-user-card:active { transform: scale(0.97); }
        .adm-user-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: rgba(60,200,161,0.15); color: #3CC8A1;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700; flex-shrink: 0;
          margin-bottom: 2px;
        }
        .adm-user-info { width: 100%; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .adm-user-name { font-size: 12px; font-weight: 700; color: #e8e8f0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
        .adm-user-meta { display: flex; gap: 4px; align-items: center; flex-wrap: nowrap; overflow: hidden; }
        .adm-user-phone { font-size: 10px; color: #888899; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
        .adm-user-date { font-size: 10px; color: #888899; white-space: nowrap; }
        .adm-user-arrow { display: none; }
        @media (min-width: 600px) {
          .adm-user-grid { grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
          .adm-user-name { font-size: 13px; }
        }
        @media (min-width: 900px) {
          .adm-user-grid { grid-template-columns: repeat(4, 1fr); }
        }

        /* ─── Product cards ─── */
        .adm-prod-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
          margin-top: 10px;
          width: 100%;
          box-sizing: border-box;
        }
        .adm-prod-card {
          background: #202024;
          border: none;
          border-radius: 12px;
          padding: 10px;
          cursor: pointer;
          text-align: left;
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
          font-family: "Montserrat", "Segoe UI", Arial, sans-serif;
          transition: transform 0.12s;
          box-shadow: 4px 4px 10px #111113, -2px -2px 8px #2c2c30;
        }
        .adm-prod-card:active { transform: scale(0.97); }
        .adm-prod-card.off { opacity: 0.42; }
        .adm-prod-status { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
        .adm-prod-dot { font-size: 9px; font-weight: 700; }
        .adm-prod-dot.on { color: #3CC8A1; }
        .adm-prod-dot.off { color: #555566; }
        .adm-prod-sku { font-size: 9px; color: #444455; }
        .adm-prod-name { font-size: 11px; font-weight: 700; color: #e8e8f0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; margin-bottom: 3px; }
        .adm-prod-price { font-size: 12px; color: #3CC8A1; font-weight: 700; }
        .adm-prod-discount { font-size: 10px; color: #888899; margin-left: 4px; }
        .adm-prod-tags { display: flex; gap: 3px; flex-wrap: wrap; margin-top: 5px; }
        .adm-prod-tag { font-size: 9px; background: rgba(60,200,161,0.1); color: #3CC8A1; border-radius: 4px; padding: 1px 5px; white-space: nowrap; }
        @media (min-width: 600px) {
          .adm-prod-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .adm-prod-name { font-size: 12px; }
        }
        @media (min-width: 900px) {
          .adm-prod-grid { grid-template-columns: repeat(4, 1fr); }
        }
      `}</style>
      <div style={styles.header}>
        {tab === 'dashboard' ? (
          <span style={styles.headerSpacer} />
        ) : (
          <button style={styles.backBtn} onClick={() => setTab('dashboard')}>
            ← Назад
          </button>
        )}
        <span style={styles.currentTabLabel}>{TAB_LABELS[tab] || 'Админка'}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => { window.location.href = telegramId ? `/discount-shop?telegram_id=${telegramId}` : '/discount-shop'; }} style={styles.btnSmall}>←</button>
          <button onClick={logout} style={styles.btnSmall}>Выйти</button>
        </div>
      </div>


      {/* ─── Dashboard Tab ─── */}
      {tab === 'dashboard' && (
        <div>
          <div style={styles.dashboardHero}>
            <div>
              <div style={styles.dashboardTitle}>Панель управления</div>
              <div style={styles.dashboardText}>Выберите раздел для настройки меню, акций, заказов и пользователей.</div>
            </div>
            <button style={styles.btnSmall} onClick={() => { loadGifts(); loadUpsell(); }}>
              ↻ Обновить
            </button>
          </div>

          <div style={styles.dashboardStats}>
            <div style={styles.dashboardStat}>
              <span style={styles.dashboardStatValue}>{upsellItems.length}/6</span>
              <span style={styles.dashboardStatLabel}>Допродажи</span>
            </div>
            <div style={styles.dashboardStat}>
              <span style={styles.dashboardStatValue}>{giftRules.promoRules.filter(r => r.enabled !== false).length}</span>
              <span style={styles.dashboardStatLabel}>Промокоды</span>
            </div>
            <div style={styles.dashboardStat}>
              <span style={styles.dashboardStatValue}>{giftRules.thresholdRules.filter(r => r.enabled !== false).length}</span>
              <span style={styles.dashboardStatLabel}>Пороги</span>
            </div>
          </div>

          <div style={styles.dashboardGrid}>
            {[
              { id: 'products', title: 'Товары', text: 'Цены, скидки, включение позиций' },
              { id: 'subscribers', title: 'Люди', text: 'Подписчики, SHC, подарки' },
              { id: 'pricing', title: 'Цены', text: 'Тарифы подписки' },
              { id: 'orders', title: 'Заказы и статистика', text: 'История заказов, выручка и графики' },
              { id: 'promo-gifts', title: 'Промокоды', text: 'Код, порог суммы и подарок' },
              { id: 'threshold-gifts', title: 'Подарок за чек', text: 'Автоподарки по порогам' },
              { id: 'upsell', title: 'Допродажи', text: 'Товары в корзине' },
              { id: 'banners', title: 'Баннеры', text: 'Слайдер на главной' },
              { id: 'stores', title: 'Точки', text: 'Самовывоз и доступность' },
              { id: 'referrals', title: 'Рефералы', text: 'Топ и история SHC' },
              { id: 'audit', title: 'Журнал', text: 'Действия персонала, клиентов и системы' },
              { id: 'funnels', title: 'Воронки', text: 'Переходы и места остановки пользователей' },
              { id: 'add', title: 'Добавить пользователя', text: 'Ручная подписка' },
              { id: 'add-product', title: 'Добавить товар', text: 'Новая позиция' },
            ].map(section => (
              <button
                key={section.id}
                style={styles.dashboardButton}
                onClick={() => setTab(section.id)}
              >
                <span style={styles.dashboardButtonTitle}>{section.title}</span>
                <span style={styles.dashboardButtonText}>{section.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Audit Log Tab ─── */}
      {tab === 'audit' && <AuditLogTab token={token} />}

      {/* ─── Product Funnels Tab ─── */}
      {tab === 'funnels' && <FunnelTab token={token} />}

      {/* ─── Products Tab ─── */}
      {tab === 'products' && (
        <div>
          {/* Catalog selector */}
          {catalogs.length > 0 && (
            <button
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '10px 14px', marginBottom: 10,
                background: AP.surface, border: `1px solid ${AP.border}`,
                borderRadius: 12, cursor: 'pointer', color: AP.text, fontWeight: 700, fontSize: 14,
              }}
              onClick={() => setCatalogMenuOpen(true)}
            >
              <span>{catalogs.find(c => c.id === activeCatalog)?.name || 'Выберите каталог'}</span>
              <span style={{ fontSize: 12, color: AP.muted }}>▾ сменить</span>
            </button>
          )}

          {productsLoading && <p style={styles.muted}>Загрузка...</p>}

          <div style={{ margin: '10px 0', padding: '8px 12px', background: '#222', borderRadius: '8px', fontSize: '13px', color: '#aaa' }}>
            Допродажи: {upsellSkus.length}/6 · промо: {giftRules.promoRules.filter(r => r.enabled !== false).length} · пороги: {giftRules.thresholdRules.filter(r => r.enabled !== false).length}
          </div>

          {currentCatalog && (
            <div className="adm-prod-grid">
              {currentCatalog.items.map((item, idx) => {
                const inUpsell = upsellSkus.includes(String(item.sku));
                const inPromo = promoGiftSkus.includes(String(item.sku));
                const inThreshold = thresholdGiftSkus.includes(String(item.sku));
                return (
                  <button
                    key={idx}
                    className={`adm-prod-card${!item.enabled ? ' off' : ''}`}
                    onClick={() => {
                      setSelectedProduct({ catalogId: currentCatalog.id, index: idx, item });
                      setEditingProduct({ price: String(item.price ?? ''), discount: item.discount != null ? String(item.discount) : '' });
                    }}
                  >
                    <div className="adm-prod-status">
                      <span className={`adm-prod-dot ${item.enabled ? 'on' : 'off'}`}>
                        {item.enabled ? '● вкл' : '○ выкл'}
                      </span>
                      {item.sku && <span className="adm-prod-sku">#{item.sku}</span>}
                    </div>
                    <div className="adm-prod-name">{item.name}</div>
                    <div>
                      <span className="adm-prod-price">{item.price} ₽</span>
                      {item.discount != null && <span className="adm-prod-discount">−{item.discount}%</span>}
                    </div>
                    {(inUpsell || inPromo || inThreshold) && (
                      <div className="adm-prod-tags">
                        {inUpsell && <span className="adm-prod-tag">допрод</span>}
                        {inPromo && <span className="adm-prod-tag">🎁 промо</span>}
                        {inThreshold && <span className="adm-prod-tag">🎁 авто</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <button onClick={loadProducts} style={{ ...styles.btnSmall, marginTop: 16 }} disabled={productsLoading}>
            Обновить
          </button>
        </div>
      )}

      {/* ─── Subscribers Tab ─── */}
      {tab === 'subscribers' && (
        <div>
          {stats && (
            <div style={styles.statsRow}>
              <span style={styles.statBadge}>Всего: {stats.total}</span>
              {Object.entries(stats.by_tariff).map(([t, count]) => (
                <span key={t} style={styles.statBadge}>{t}₽: {count}</span>
              ))}
              <span style={styles.statBadge}>Активных: {stats.active}</span>
              <span style={styles.statBadge}>Амбассадоров: {stats.ambassadors}</span>
            </div>
          )}

          <div style={styles.filterRow}>
            <select value={subsFilter} onChange={e => setSubsFilter(e.target.value)} style={styles.select}>
              <option value="all">Все тарифы</option>
              <option value="290">290₽</option>
              <option value="490">490₽</option>
              <option value="1190">1190₽</option>
              <option value="9990">9990₽ (Амбасс.)</option>
            </select>
            <input
              type="text"
              placeholder="Поиск имени, телефона, ID..."
              value={subsSearch}
              onChange={e => setSubsSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {subsLoading && <p style={styles.muted}>Загрузка...</p>}
          <div style={styles.subsCount}>Показано: {filteredSubs.length}</div>

          <div className="adm-user-grid">
            {filteredSubs.map(s => (
              <button
                key={s.telegram_id}
                className="adm-user-card"
                onClick={() => setSelectedUser(s)}
              >
                <div className="adm-user-avatar">
                  {(s.first_name || s.name || '?')[0].toUpperCase()}
                </div>
                <div className="adm-user-info">
                  <div className="adm-user-name">{s.first_name || s.name || 'Без имени'}</div>
                  <div className="adm-user-meta">
                    <span style={{ ...styles.tariffBadge(s.tariff), fontSize: 9, padding: '1px 5px' }}>{s.tariff}₽</span>
                    <span style={{ fontSize: 9, color: s.subscription_status === 'активно' ? AP.accent : AP.muted }}>
                      {s.subscription_status === 'активно' ? '●' : '○'}
                    </span>
                  </div>
                  {s.phone && <div className="adm-user-phone">{s.phone}</div>}
                  {s.subscription_end && <div className="adm-user-date">до {s.subscription_end}</div>}
                </div>
              </button>
            ))}
          </div>

          <button onClick={loadSubscribers} style={{ ...styles.btnSmall, marginTop: 16 }} disabled={subsLoading}>
            Обновить
          </button>
        </div>
      )}

      {/* ─── Banners Tab ─── */}
      {tab === 'banners' && (
        <div>
          {bannersLoading && <p style={styles.muted}>Загрузка...</p>}

          <div style={{ padding: '8px 10px', background: 'rgba(60,200,161,0.08)', borderRadius: 10, marginBottom: 10, fontSize: 11, color: '#9fb0c3', lineHeight: 1.5, border: '1px solid rgba(60,200,161,0.2)' }}>
            Рекомендуемый размер: <b style={{ color: '#3CC8A1' }}>1200×450 px</b> (соотношение 8:3).
            Картинка будет автоматически обрезана по центру до нужных пропорций.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {banners.map((banner, idx) => {
              const slot = banner.id;
              const hasImage = banner && banner.image;
              return (
                <div key={slot} style={styles.bannerSlot}>
                  <div style={styles.bannerHeader}>
                    <span style={{ color: '#e8e8f0', fontSize: 13, fontWeight: 600 }}>Баннер {slot}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <label style={styles.grantBtn}>
                        {bannerUploading === slot ? '...' : 'Загрузить'}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={e => { if (e.target.files[0]) uploadBanner(slot, e.target.files[0]); }}
                          disabled={bannerUploading === slot}
                        />
                      </label>
                      {hasImage && (
                        <button style={styles.claimBtn} onClick={() => deleteBanner(slot)} disabled={!!bannerUploading}>
                          Удалить
                        </button>
                      )}
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Ссылка (опционально)"
                    value={banner.link || ''}
                    onChange={e => {
                      const updated = banners.map(b => b.id === slot ? { ...b, link: e.target.value } : b);
                      setBanners(updated);
                    }}
                    onBlur={() => {
                      fetch(`${API}/api/admin/banners`, {
                        method: 'POST',
                        headers: headers(),
                        body: JSON.stringify({ action: 'set-all', banners }),
                      }).catch(() => {});
                    }}
                    style={{ ...styles.grantInput, marginBottom: 8, fontSize: 12 }}
                  />
                  <div style={styles.bannerPreview}>
                    {hasImage ? (
                      <img src={banner.image + '?v=' + Date.now()} alt="" style={styles.bannerImg} />
                    ) : (
                      <div style={{ ...styles.bannerEmpty, background: banner?.color || '#f5f5f5' }}>
                        Пустой слот
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {banners.length < 7 && (
              <button style={styles.grantBtn} onClick={addBannerSlot} disabled={!!bannerUploading}>
                + Добавить слот
              </button>
            )}
            {banners.length > 1 && (
              <button style={styles.claimBtn} onClick={removeBannerSlot} disabled={!!bannerUploading}>
                − Удалить последний
              </button>
            )}
            <button onClick={loadBanners} style={styles.btnSmall} disabled={bannersLoading}>
              Обновить
            </button>
          </div>
        </div>
      )}

      {/* ─── Pricing Tab ─── */}
      {tab === 'pricing' && (
        <div>
          {pricingLoading && <p style={styles.muted}>Загрузка...</p>}

          {pricing && Object.entries(pricing).map(([tarif, data]) => (
            <div key={tarif} style={{ ...styles.bannerSlot, marginBottom: 12 }}>
              <div style={{ color: '#e8e8f0', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                Тариф {tarif}₽ {tarif === '9990' ? '(Амбассадор)' : ''}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={styles.priceField}>
                  <label style={styles.priceLabel}>1 мес</label>
                  <input
                    type="number"
                    value={data.months?.[1] || data.price || ''}
                    onChange={e => updatePrice(tarif, 'price', e.target.value)}
                    style={styles.priceInput2}
                  />
                </div>
                {tarif !== '9990' && (
                  <>
                    <div style={styles.priceField}>
                      <label style={{ ...styles.priceLabel, color: '#3CC8A1' }}>3 мес ↺</label>
                      <input
                        type="number"
                        value={data.months?.[3] || ''}
                        onChange={e => updatePrice(tarif, '3', e.target.value)}
                        style={styles.priceInput2}
                      />
                    </div>
                    <div style={styles.priceField}>
                      <label style={{ ...styles.priceLabel, color: '#3CC8A1' }}>5 мес ↺</label>
                      <input
                        type="number"
                        value={data.months?.[5] || ''}
                        onChange={e => updatePrice(tarif, '5', e.target.value)}
                        style={styles.priceInput2}
                      />
                    </div>
                  </>
                )}
              </div>
              {tarif !== '9990' && (
                <div style={{ fontSize: 11, color: AP.muted, marginTop: 4 }}>
                  ↺ пересчитываются автоматически при смене цены за 1 мес (−14% и −17%)
                </div>
              )}
            </div>
          ))}

          <button
            style={styles.btnPrimary}
            onClick={savePricing}
            disabled={pricingSaving}
          >
            {pricingSaving ? 'Сохранение...' : 'Сохранить цены'}
          </button>
        </div>
      )}

      {/* ─── Add User Tab ─── */}
      {tab === 'add' && (
        <div>
          <div style={styles.addCard}>
            <h3 style={styles.addTitle}>Добавить пользователя</h3>
            <form onSubmit={addUser}>
              <label style={styles.fieldLabel}>Имя (необязательно)</label>
              <input
                type="text"
                placeholder="Имя клиента"
                value={addName}
                onChange={e => setAddName(e.target.value)}
                style={styles.input}
              />

              <label style={styles.fieldLabel}>Телефон *</label>
              <input
                type="tel"
                placeholder="+7 900 000 00 00"
                value={addPhone}
                onChange={e => setAddPhone(e.target.value)}
                style={styles.input}
                required
              />

              <label style={styles.fieldLabel}>Тариф *</label>
              <select
                value={addTariff}
                onChange={e => setAddTariff(e.target.value)}
                style={styles.input}
              >
                <option value="290">290 ₽ — Скидки 30/20%</option>
                <option value="490">490 ₽ — Скидки + бесплатные роллы</option>
                <option value="1190">1190 ₽ — Скидки + сеты + кофе</option>
                <option value="9990">9990 ₽ — Амбассадор</option>
              </select>

              <label style={styles.fieldLabel}>Подарочные роллы (шт.)</label>
              <input
                type="number"
                min="0"
                max="50"
                placeholder="0"
                value={addGiftRolls}
                onChange={e => setAddGiftRolls(e.target.value)}
                style={styles.input}
              />

              <label style={styles.fieldLabel}>Подарочные сеты (шт.)</label>
              <input
                type="number"
                min="0"
                max="50"
                placeholder="0"
                value={addGiftSets}
                onChange={e => setAddGiftSets(e.target.value)}
                style={styles.input}
              />

              <label style={styles.fieldLabel}>Подписка активна до *</label>
              <input
                type="date"
                value={addEndDate}
                onChange={e => setAddEndDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{ ...styles.input, marginBottom: 18, colorScheme: 'dark', maxWidth: '100%', WebkitAppearance: 'none', appearance: 'none' }}
                required
              />

              <button
                type="submit"
                style={styles.btnPrimary}
                disabled={addLoading || !addPhone.trim()}
              >
                {addLoading ? 'Добавление...' : 'Добавить пользователя'}
              </button>
            </form>

            {addResult && (
              <div style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 10,
                background: addResult.success ? 'rgba(60,200,161,0.1)' : 'rgba(255,77,106,0.1)',
                color: addResult.success ? '#3CC8A1' : '#ff4d6a',
                fontSize: 13,
              }}>
                <div style={{ fontWeight: 700, marginBottom: addResult.success ? 8 : 0 }}>
                  {addResult.success ? '✓ ' : '✗ '}{addResult.message || addResult.error}
                </div>
                {addResult.success && addResult.user && (
                  <div style={{ fontSize: 12, color: '#9fb0c3', lineHeight: 1.7 }}>
                    <div><b>Имя:</b> {addResult.user.name}</div>
                    <div><b>Телефон:</b> {addResult.user.phone}</div>
                    <div><b>Тариф:</b> {addResult.user.tariff} ₽</div>
                    <div><b>Период:</b> {addResult.user.subscription_start} — {addResult.user.subscription_end}</div>
                    {addResult.gifts_granted > 0 && (
                      <div><b>Подарки:</b> {addResult.gifts_granted} шт. выдано</div>
                    )}
                    <div style={{ color: '#999', marginTop: 2 }}>ID: {addResult.user.telegram_id}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Stats Section ─── */}
      {tab === 'orders' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={styles.addTitle}>Статистика</h3>
            <button style={styles.btnSmall} onClick={loadStats} disabled={dashLoading}>
              {dashLoading ? '...' : 'Обновить'}
            </button>
          </div>
          {dashStats && (
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{dashStats.totalUsers}</div>
                <div style={styles.statLabel}>Всего в базе</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{dashStats.activeTotal}</div>
                <div style={styles.statLabel}>Активных</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{dashStats.expiringSoon}</div>
                <div style={styles.statLabel}>Истекает ≤7 дн.</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{dashStats.newThisWeek}</div>
                <div style={styles.statLabel}>Новых за 7 дн.</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{dashStats.revenueThisMonth?.toLocaleString('ru-RU')} ₽</div>
                <div style={styles.statLabel}>Выручка за месяц</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statVal}>{dashStats.ambassadors}</div>
                <div style={styles.statLabel}>Амбассадоров</div>
              </div>
              <div style={{ ...styles.statCard, gridColumn: '1 / -1' }}>
                <div style={styles.statLabel}>По тарифам (активные)</div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  {Object.entries(dashStats.activeByTariff).map(([t, n]) => (
                    <span key={t} style={{ fontWeight: 700, fontSize: 15 }}>
                      {t}₽: <span style={{ color: '#3CC8A1' }}>{n}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {dashStats?.periods && (
            <>
              <h4 style={{ ...styles.addTitle, marginTop: 22, fontSize: 14 }}>За период</h4>
              <div style={styles.statsGrid}>
                {[
                  { key: '1d',  label: '1 день' },
                  { key: '7d',  label: '7 дней' },
                  { key: '15d', label: '15 дней' },
                  { key: '30d', label: '30 дней' },
                ].map(({ key, label }) => {
                  const p = dashStats.periods[key] || {};
                  return (
                    <div key={key} style={{ ...styles.statCard, gridColumn: '1 / -1' }}>
                      <div style={{ ...styles.statLabel, fontSize: 12, color: '#3CC8A1', letterSpacing: '0.08em', marginBottom: 10 }}>
                        {label.toUpperCase()}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                        <div>
                          <div style={{ ...styles.statVal, fontSize: 18 }}>
                            {(p.subsRevenue || 0).toLocaleString('ru-RU')} ₽
                          </div>
                          <div style={styles.statLabel}>Подписки · {p.newSubs || 0} опл.</div>
                        </div>
                        <div>
                          <div style={{ ...styles.statVal, fontSize: 18 }}>
                            {(p.ordersRevenue || 0).toLocaleString('ru-RU')} ₽
                          </div>
                          <div style={styles.statLabel}>Заказы · {p.orders || 0} шт.</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <div style={{ ...styles.statVal, fontSize: 18 }}>{p.promoGifts || 0}</div>
                          <div style={styles.statLabel}>Промо-подарки</div>
                        </div>
                        <div>
                          <div style={{ ...styles.statVal, fontSize: 18 }}>{p.thresholdGifts || 0}</div>
                          <div style={styles.statLabel}>Подарки за порог</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {dashStats?.giftUsage && (
            <>
              <h4 style={{ ...styles.addTitle, marginTop: 22, fontSize: 14 }}>Промо и подарки за 30 дней</h4>
              <div style={styles.statsGrid}>
                <div style={{ ...styles.statCard, gridColumn: '1 / -1' }}>
                  <div style={{ ...styles.statLabel, marginBottom: 10 }}>ПРОМОКОДЫ</div>
                  {(dashStats.giftUsage.promoCodes || []).length === 0 ? (
                    <div style={styles.statLabel}>Нет применений</div>
                  ) : (
                    dashStats.giftUsage.promoCodes.map(item => (
                      <div key={`${item.code}-${item.source}`} style={styles.giftStatRow}>
                        <div>
                          <div style={styles.giftStatTitle}>{item.code}</div>
                          <div style={styles.giftStatText}>
                            {(item.gifts || []).map(g => `${g.name}${g.uses > 1 ? ` x${g.uses}` : ''}`).join(', ')}
                          </div>
                        </div>
                        <div style={styles.giftStatCount}>{item.uses}</div>
                      </div>
                    ))
                  )}
                </div>

                <div style={{ ...styles.statCard, gridColumn: '1 / -1' }}>
                  <div style={{ ...styles.statLabel, marginBottom: 10 }}>ПОДАРКИ ЗА ЧЕК</div>
                  {(dashStats.giftUsage.thresholdGifts || []).length === 0 ? (
                    <div style={styles.statLabel}>Нет применений</div>
                  ) : (
                    dashStats.giftUsage.thresholdGifts.map(item => (
                      <div key={`${item.threshold || 'rule'}-${item.source}`} style={styles.giftStatRow}>
                        <div>
                          <div style={styles.giftStatTitle}>
                            {item.threshold ? `${item.threshold.toLocaleString('ru-RU')} ₽` : item.source}
                          </div>
                          <div style={styles.giftStatText}>
                            {(item.gifts || []).map(g => `${g.name}${g.uses > 1 ? ` x${g.uses}` : ''}`).join(', ')}
                          </div>
                        </div>
                        <div style={styles.giftStatCount}>{item.uses}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          {Array.isArray(dashStats?.ordersDaily) && dashStats.ordersDaily.length > 0 && (() => {
            const daily = dashStats.ordersDaily;
            const maxCount = Math.max(1, ...daily.map(d => d.count || 0));
            const W = 560;
            const H = 140;
            const padL = 8;
            const padR = 8;
            const padT = 8;
            const padB = 22;
            const barW = (W - padL - padR) / daily.length;
            return (
              <div style={{ ...styles.statCard, marginTop: 16 }}>
                <div style={{ ...styles.statLabel, marginBottom: 8 }}>
                  Заказы по дням (30 дней) · макс {maxCount}
                </div>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
                  {daily.map((d, i) => {
                    const h = ((d.count || 0) / maxCount) * (H - padT - padB);
                    const x = padL + i * barW;
                    const y = H - padB - h;
                    return (
                      <g key={d.date}>
                        <rect
                          x={x + 1}
                          y={y}
                          width={Math.max(1, barW - 2)}
                          height={h}
                          fill="#3CC8A1"
                          opacity={0.85}
                        >
                          <title>{`${d.date}: ${d.count} заказов, ${(d.revenue || 0).toLocaleString('ru-RU')} ₽`}</title>
                        </rect>
                      </g>
                    );
                  })}
                  {daily.length > 0 && (
                    <>
                      <text x={padL} y={H - 6} fontSize="10" fill="#888">{daily[0].date.slice(5)}</text>
                      <text x={W - padR} y={H - 6} fontSize="10" fill="#888" textAnchor="end">
                        {daily[daily.length - 1].date.slice(5)}
                      </text>
                    </>
                  )}
                </svg>
              </div>
            );
          })()}

          {!dashStats && !dashLoading && <p style={styles.muted}>Нажмите «Обновить»</p>}
        </div>
      )}

      {/* ─── Add Product Tab ─── */}
      {tab === 'add-product' && (
        <div>
          <div style={styles.addCard}>
            <h3 style={styles.addTitle}>Добавить позицию</h3>

            {/* Выбор категории — 5 кнопок */}
            <label style={styles.fieldLabel}>Категория</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {[
                { id: 'rolls',    label: 'Холодные роллы' },
                { id: 'zaproll', label: 'Запечённые' },
                { id: 'sets',    label: 'Сеты' },
                { id: 'gift-roll', label: '🎁 Подарочный ролл' },
                { id: 'gift-set',  label: '🎁 Подарочный сет' },
              ].map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  style={newCatId === cat.id ? styles.catBtnActive : styles.catBtn}
                  onClick={() => { setNewCatId(cat.id); setNewAddToSub(false); setNewSubSku(''); }}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <form onSubmit={addProduct}>
              <label style={styles.fieldLabel}>Название *</label>
              <input
                type="text"
                placeholder="Название товара"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                style={styles.input}
                required
              />

              <label style={styles.fieldLabel}>
                {isGiftCat(newCatId) ? 'ID в Frontpad *' : 'Артикул Frontpad *'}
              </label>
              <input
                type="text"
                placeholder="Например: 1234"
                value={newSku}
                onChange={e => setNewSku(e.target.value)}
                style={styles.input}
                required
              />

              {/* Цена — только для обычных категорий */}
              {!isGiftCat(newCatId) && (
                <>
                  <label style={styles.fieldLabel}>Цена без скидки (₽) *</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Например: 750"
                    value={newPrice}
                    onChange={e => setNewPrice(e.target.value)}
                    style={styles.input}
                    required
                  />
                </>
              )}

              {isGiftCat(newCatId) && (
                <div style={{ padding: '8px 12px', background: 'rgba(60,200,161,0.07)', borderRadius: 8, marginBottom: 10, fontSize: 12, color: AP.muted }}>
                  Цена: <b style={{ color: AP.accent }}>0 ₽</b> (подарок)
                </div>
              )}

              <label style={styles.fieldLabel}>Описание (необязательно)</label>
              <input
                type="text"
                placeholder="Состав или краткое описание"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                style={styles.input}
              />

              <label style={styles.fieldLabel}>Фото товара</label>
              <label style={{ ...styles.grantBtn, display: 'inline-block', marginBottom: 10, cursor: 'pointer' }}>
                Выбрать фото
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files[0]) handleNewProductImage(e.target.files[0]); }}
                />
              </label>
              {newImagePreview && (
                <div style={{ marginBottom: 10 }}>
                  <img
                    src={newImagePreview}
                    alt="preview"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, boxShadow: NEU.card }}
                  />
                </div>
              )}

              {/* Скидочный каталог — только для обычных категорий */}
              {!isGiftCat(newCatId) && (
                <div style={{ padding: '10px 12px', background: 'rgba(60,200,161,0.06)', borderRadius: 10, marginBottom: 10, border: '1px solid rgba(60,200,161,0.15)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: newAddToSub ? 8 : 0 }}>
                    <input
                      type="checkbox"
                      checked={newAddToSub}
                      onChange={e => setNewAddToSub(e.target.checked)}
                    />
                    <span style={{ fontSize: 12, color: AP.text, fontWeight: 600 }}>Добавить в скидочный магазин</span>
                  </label>
                  {newAddToSub && (
                    <>
                      <label style={styles.fieldLabel}>Артикул для скидочного каталога *</label>
                      <input
                        type="text"
                        placeholder="4-значный артикул"
                        value={newSubSku}
                        onChange={e => setNewSubSku(e.target.value)}
                        style={{ ...styles.input, marginBottom: 0 }}
                      />
                    </>
                  )}
                </div>
              )}

              <button
                type="submit"
                style={styles.btnPrimary}
                disabled={
                  newProductLoading || !newName.trim() || !newSku.trim() ||
                  (!isGiftCat(newCatId) && !newPrice)
                }
              >
                {newProductLoading ? 'Добавление...' : 'Добавить позицию'}
              </button>
            </form>

            {newProductResult && (
              <div style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 10,
                background: newProductResult.success ? 'rgba(60,200,161,0.1)' : 'rgba(255,77,106,0.1)',
                color: newProductResult.success ? AP.accent : AP.danger,
                fontSize: 13,
              }}>
                <div style={{ fontWeight: 700 }}>
                  {newProductResult.success
                    ? `✓ Добавлено: ${newProductResult.name}`
                    : `✗ ${newProductResult.error}`}
                </div>
                {newProductResult.addedTo && (
                  <div style={{ fontSize: 11, color: AP.muted, marginTop: 4 }}>
                    Каталог: {newProductResult.addedTo.join(', ')}
                  </div>
                )}
                {newProductResult.imagePath && (
                  <div style={{ fontSize: 11, color: AP.muted, marginTop: 2 }}>
                    Фото: {newProductResult.imagePath}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Orders Section ─── */}
      {tab === 'orders' && (
        <div style={{ marginTop: 22 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={styles.addTitle}>Заказы подписчиков</h3>
            <button style={styles.btnSmall} onClick={loadGiftOrders} disabled={giftOrdersLoading}>
              {giftOrdersLoading ? '...' : 'Обновить'}
            </button>
          </div>
          <input
            style={{ ...styles.input, marginBottom: 10, fontSize: 12 }}
            placeholder="Поиск по имени, телефону, адресу..."
            value={giftOrdersSearch}
            onChange={e => setGiftOrdersSearch(e.target.value)}
          />
          {giftOrdersLoading && <p style={styles.muted}>Загрузка...</p>}
          {giftOrders && (() => {
            const q = giftOrdersSearch.trim().toLowerCase();
            const filtered = q
              ? giftOrders.filter(o =>
                  (o.client_name || o.user_name || '').toLowerCase().includes(q) ||
                  (o.user_phone || '').includes(q) ||
                  (o.address || '').toLowerCase().includes(q)
                )
              : giftOrders;
            return filtered.length === 0
              ? <p style={styles.muted}>Нет заказов</p>
              : filtered.map(o => {
                let products = [];
                try { products = JSON.parse(o.products_json || '[]'); } catch {}
                const productNames = products.map(p => p.name).filter(Boolean).join(', ') || '—';
                const date = o.created_at ? new Date(o.created_at).toLocaleDateString('ru-RU') : '—';
                const isGift = o.order_type === 'gift';
                return (
                  <div key={o.id} style={{ ...styles.subCard, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: isGift ? CP.green : CP.cyan, fontWeight: 700, fontSize: 13 }}>
                        {productNames}
                      </span>
                      <span style={{ color: CP.muted, fontSize: 12 }}>{date}</span>
                    </div>
                    <div style={{ fontSize: 13, color: CP.text, marginBottom: 2 }}>
                      {o.client_name || o.user_name || '—'}
                      {o.user_tariff && <span style={{ color: CP.muted, marginLeft: 6, fontSize: 11 }}>{o.user_tariff}₽</span>}
                      <span style={{ color: isGift ? CP.green : CP.cyan, marginLeft: 6, fontSize: 11 }}>
                        {isGift ? '🎁 Подарок' : '🏷 Со скидкой'}
                      </span>
                      {o.total_price > 0 && <span style={{ color: CP.muted, marginLeft: 6, fontSize: 11 }}>{o.total_price}₽</span>}
                    </div>
                    {o.user_phone && <div style={{ fontSize: 12, color: CP.muted }}>{o.user_phone}</div>}
                    {o.address && <div style={{ fontSize: 12, color: CP.yellow, marginTop: 2 }}>
                      {o.delivery_type === 'delivery' ? '🚗 ' : '🏪 '}{o.address}
                    </div>}
                  </div>
                );
              });
          })()}
          {!giftOrders && !giftOrdersLoading && <p style={styles.muted}>Нажмите «Обновить»</p>}
        </div>
      )}

      {/* ─── Referrals Tab ─── */}
      {tab === 'referrals' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ color: AP.text, fontWeight: 600 }}>Реферальная система</span>
            <button style={styles.btnSmall} onClick={loadReferralData} disabled={referralLoading}>
              {referralLoading ? '...' : '↺ Обновить'}
            </button>
          </div>

          {referralLoading && <p style={styles.muted}>Загрузка...</p>}

          {referralData && (() => {
            const searchLow = referralSearch.toLowerCase();
            const filteredTop = referralData.topReferrers.filter(r =>
              !searchLow ||
              (r.name || '').toLowerCase().includes(searchLow) ||
              (r.phone || '').includes(referralSearch) ||
              String(r.telegram_id).includes(referralSearch)
            );

            return (
              <>
                {/* Поиск */}
                <input
                  style={{ ...styles.input, marginBottom: 14, width: '100%', boxSizing: 'border-box' }}
                  placeholder="Поиск по имени, телефону..."
                  value={referralSearch}
                  onChange={e => setReferralSearch(e.target.value)}
                />

                {/* Топ рефереров */}
                <div style={{ color: AP.accent, fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                  👥 Топ рефереров ({filteredTop.length})
                </div>
                {filteredTop.length === 0 && <p style={styles.muted}>Нет данных</p>}
                {filteredTop.map(r => (
                  <div key={r.telegram_id} style={{ ...styles.subCard, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <span style={{ color: AP.text, fontWeight: 600 }}>{r.name || '—'}</span>
                        {r.tariff && (
                          <span style={{ ...styles.badge, marginLeft: 8 }}>{r.tariff}₽</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', fontSize: 12 }}>
                        <div style={{ color: AP.accent }}>👥 {r.referrals_count} реф.</div>
                        <div style={{ color: AP.yellow }}>💎 {Math.round(r.shc_earned)} SHC</div>
                      </div>
                    </div>
                    {r.phone && <div style={{ fontSize: 12, color: AP.muted, marginTop: 2 }}>{r.phone}</div>}
                    <div style={{ fontSize: 11, color: AP.muted }}>ID: {r.telegram_id} · баланс: {Math.round(r.balance_shc || 0)} SHC</div>
                  </div>
                ))}

                {/* Последние начисления */}
                <div style={{ color: AP.accent, fontWeight: 600, margin: '18px 0 8px', fontSize: 13 }}>
                  💎 Последние начисления SHC ({referralData.recentBonuses.length})
                </div>
                {referralData.recentBonuses.filter(b =>
                  !searchLow ||
                  (b.user_name || '').toLowerCase().includes(searchLow) ||
                  (b.referral_name || '').toLowerCase().includes(searchLow) ||
                  String(b.user_id).includes(referralSearch)
                ).map(b => {
                  const isSubscription = b.friends_count === 0;
                  return (
                    <div key={b.id} style={{ ...styles.subCard, marginBottom: 6, padding: '8px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 13 }}>
                          <span style={{ color: isSubscription ? AP.warn : AP.accent }}>
                            {isSubscription ? '📈' : '🤝'} +{Math.round(b.total_amount)} SHC
                          </span>
                          <span style={{ color: AP.muted, marginLeft: 8, fontSize: 11 }}>
                            {b.achievement || (isSubscription ? '20% от подписки' : `${b.friends_count} друг`)}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: AP.muted }}>
                          {(b.created_at || '').slice(0, 10)}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: AP.muted, marginTop: 3 }}>
                        <span style={{ color: AP.text }}>{b.user_name || b.user_id}</span>
                        {' ← '}
                        <span>{b.referral_name || b.referral_id}</span>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          {!referralData && !referralLoading && <p style={styles.muted}>Нажмите «Обновить»</p>}
        </div>
      )}

      {/* ─── Gift Rules Tabs ─── */}
      {(tab === 'promo-gifts' || tab === 'threshold-gifts') && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ color: AP.text, fontWeight: 700, fontSize: 14 }}>
              {tab === 'promo-gifts' ? 'Промокоды' : 'Подарок за чек'}
            </span>
            <button style={styles.btnSmall} onClick={loadGifts} disabled={giftRulesLoading}>
              {giftRulesLoading ? '...' : '↻ Обновить'}
            </button>
          </div>

          <div style={styles.giftRuleForm}>
            <div style={styles.giftRuleFormTitle}>
              {giftRuleForm.id ? 'Редактировать правило' : (tab === 'promo-gifts' ? 'Новый промокод' : 'Новый порог')}
            </div>
            {tab === 'promo-gifts' && (
              <div style={{ ...styles.muted, marginBottom: 10 }}>
                Чтобы один промокод выдавал несколько подарков, добавьте несколько правил с одинаковым кодом и разными подарками.
              </div>
            )}
            <div style={styles.giftRuleGrid}>
              {tab === 'promo-gifts' && (
                <input
                  style={styles.input}
                  value={giftRuleForm.code}
                  onChange={e => setGiftRuleForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="Промокод"
                  maxLength={30}
                />
              )}
              <input
                style={styles.input}
                type="number"
                min="1"
                value={giftRuleForm.threshold}
                onChange={e => setGiftRuleForm(prev => ({ ...prev, threshold: e.target.value }))}
                placeholder={tab === 'promo-gifts' ? 'Сумма для промокода' : 'Порог чека'}
              />
              <select
                style={styles.select}
                value={giftRuleForm.sku}
                onChange={e => setGiftRuleForm(prev => ({ ...prev, sku: e.target.value }))}
              >
                <option value="">Выберите подарок</option>
                {giftProductOptions.map(item => (
                  <option key={`${item.catalog}-${item.sku}`} value={item.sku}>
                    {item.name} · #{item.sku}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <button
                style={styles.btnSave}
                onClick={saveGiftRule}
                disabled={giftRuleSaving === 'form'}
              >
                {giftRuleSaving === 'form' ? '...' : (giftRuleForm.id ? 'Сохранить' : 'Добавить')}
              </button>
              {giftRuleForm.id && (
                <button style={styles.btnCancel} onClick={resetGiftRuleForm}>
                  Отмена
                </button>
              )}
            </div>
            {giftProductOptions.length === 0 && (
              <div style={{ ...styles.muted, marginTop: 8 }}>
                Сначала загрузите товары, чтобы выбрать подарок.
              </div>
            )}
          </div>

          {(tab === 'promo-gifts' ? giftRules.promoRules : giftRules.thresholdRules).length === 0 ? (
            <p style={styles.muted}>{tab === 'promo-gifts' ? 'Промокодов пока нет.' : 'Порогов пока нет.'}</p>
          ) : (
            <div style={styles.giftRuleList}>
              {(tab === 'promo-gifts'
                ? giftRules.promoRules.map(rule => ({ ...rule, type: 'promo' }))
                : giftRules.thresholdRules.map(rule => ({ ...rule, type: 'threshold' })))
                .sort((a, b) => Number(a.threshold) - Number(b.threshold))
                .map(rule => (
                  <div key={rule.id} style={rule.enabled === false ? styles.giftRuleCardOff : styles.giftRuleCard}>
                    <div style={styles.giftRuleHead}>
                      <div>
                        <div style={styles.giftRuleName}>
                          {rule.type === 'promo' ? `Промокод ${rule.code}` : `Порог ${rule.threshold}₽`}
                        </div>
                        <div style={styles.giftRuleMeta}>
                          {rule.type === 'promo' ? `От ${rule.threshold}₽ · один подарок из группы кода` : 'Автоматически'} · {rule.product?.name || `SKU ${rule.sku}`}
                        </div>
                      </div>
                      <span style={rule.enabled === false ? styles.giftRuleBadgeOff : styles.giftRuleBadge}>
                        {rule.enabled === false ? 'выкл' : 'вкл'}
                      </span>
                    </div>
                    <div style={styles.giftRuleActions}>
                      <button
                        style={styles.btnSmall}
                        onClick={() => editGiftRule(rule, rule.type)}
                      >
                        Изменить
                      </button>
                      <button
                        style={rule.enabled === false ? styles.giftBtnAdd : styles.giftBtnRemove}
                        onClick={() => toggleGiftRule(rule, rule.type)}
                        disabled={giftRuleSaving === rule.id}
                      >
                        {giftRuleSaving === rule.id ? '...' : (rule.enabled === false ? 'Включить' : 'Выключить')}
                      </button>
                      <button
                        style={{ ...styles.toggleOff, fontSize: 10, padding: '5px 10px' }}
                        onClick={() => deleteGiftRule(rule)}
                        disabled={giftRuleSaving === rule.id}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Upsell Tab ─── */}
      {tab === 'upsell' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ color: AP.text, fontWeight: 700, fontSize: 14 }}>
              Допродажи в корзине
            </span>
            <span style={{ color: AP.accent, fontSize: 12, fontWeight: 600 }}>
              {upsellItems.length}/6
            </span>
            <button style={styles.btnSmall} onClick={loadUpsell}>↺ Обновить</button>
            {upsellItems.length > 0 && (
              <button
                style={{ ...styles.btnSmall, color: AP.danger, borderColor: AP.danger }}
                onClick={clearAllUpsell}
                disabled={upsellClearing}
              >
                {upsellClearing ? '...' : '✕ Очистить всё'}
              </button>
            )}
          </div>

          <p style={{ ...styles.muted, marginBottom: 14, fontSize: 12 }}>
            Товары из этого списка отображаются в корзине пользователя как «Добавьте к заказу».
            Добавлять товары можно кнопкой «☆ В допродажи» во вкладке «Товары».
          </p>

          {upsellItems.length === 0 ? (
            <p style={styles.muted}>Список пуст. Добавьте товары во вкладке «Товары».</p>
          ) : (
            <div style={styles.productList}>
              {upsellItems.map((item) => (
                <div key={item.sku} style={styles.productRow}>
                  <div style={styles.productInfo}>
                    <span style={styles.productName}>{item.name}</span>
                    <span style={styles.sku}>SKU: {item.sku}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 4 }}>
                    <span style={{ ...styles.price, cursor: 'default' }}>{item.price} ₽</span>
                    <button
                      style={{ ...styles.toggleOff, fontSize: 10, padding: '3px 8px' }}
                      onClick={() => toggleUpsell(item.sku, true)}
                      disabled={upsellSaving === item.sku}
                    >
                      {upsellSaving === item.sku ? '...' : '✕ Убрать'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

{/* ─── Stores Tab ─── */}
      {tab === 'stores' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ color: AP.text, fontWeight: 700, fontSize: 14 }}>📍 Точки самовывоза</span>
            <button style={styles.btnSmall} onClick={loadStores} disabled={storesLoading}>
              {storesLoading ? '...' : '↺ Обновить'}
            </button>
          </div>

          <p style={{ ...styles.muted, marginBottom: 16, fontSize: 12 }}>
            Выключенные точки не отображаются в форме заказа. Изменения вступают в силу сразу.
          </p>

          {!storesState && !storesLoading && (
            <p style={styles.muted}>Нажмите «Обновить» для загрузки</p>
          )}
          {storesLoading && <p style={styles.muted}>Загрузка...</p>}

          {storesState && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { id: '1', address: 'ул. Ю.Гагарина, д. 16Б' },
                { id: '2', address: 'ул. Согласия, д. 46' },
                { id: '3', address: 'ул. Автомобильная, д. 12Б' },
                { id: '4', address: 'Гурьевск' },
              ].map(({ id, address }) => {
                const enabled = storesState.points[id] !== false;
                const isSaving = storesSaving === id;
                return (
                  <div
                    key={id}
                    style={{
                      ...styles.subCard,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 16px', gap: 12,
                      opacity: enabled ? 1 : 0.55,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: AP.text }}>{address}</div>
                      <div style={{ fontSize: 12, color: enabled ? AP.accent : AP.danger, marginTop: 2 }}>
                        {enabled ? '● Работает' : '○ Выключена'}
                      </div>
                    </div>
                    <button
                      disabled={isSaving}
                      onClick={() => toggleStore(id, enabled)}
                      style={{
                        padding: '9px 18px', borderRadius: 10, border: 'none', cursor: isSaving ? 'default' : 'pointer',
                        fontWeight: 700, fontSize: 13,
                        background: enabled ? 'rgba(255,77,106,0.12)' : 'rgba(60,200,161,0.12)',
                        color: enabled ? AP.danger : AP.accent,
                        flexShrink: 0,
                      }}
                    >
                      {isSaving ? '...' : (enabled ? 'Выключить' : 'Включить')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

{/* ─── Шторка выбора каталога ─── */}
      {catalogMenuOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 198, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => setCatalogMenuOpen(false)}
          />
          <div style={styles.menuSheet}>
            <div style={styles.menuHandle} />
            <div style={{ fontSize: 11, fontWeight: 700, color: AP.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8, paddingLeft: 4 }}>
              Каталог
            </div>
            {catalogs.map(({ id, name, itemCount }) => (
              <button
                key={id}
                style={activeCatalog === id ? styles.menuItemActive : styles.menuItem}
                onClick={() => { setActiveCatalog(id); setCatalogMenuOpen(false); }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: activeCatalog === id ? AP.accent : AP.text }}>{name}</span>
                  <span style={{ fontSize: 12, color: AP.muted, marginTop: 1 }}>{itemCount} товаров</span>
                </div>
                {activeCatalog === id && <span style={{ fontSize: 14, color: AP.accent, fontWeight: 700 }}>✓</span>}
              </button>
            ))}
          </div>
        </>
      )}

{/* ─── Шторка товара ─── */}
{selectedProduct && (
  <>
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 198, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={() => setSelectedProduct(null)}
    />
    <div style={{ ...styles.userSheet, zIndex: 199 }}>
      <div style={styles.menuHandle} />

      <div style={styles.sheetHeader}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: AP.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedProduct.item.name}
          </div>
          {selectedProduct.item.sku && (
            <div style={{ fontSize: 11, color: AP.muted, marginTop: 2 }}>SKU #{selectedProduct.item.sku}</div>
          )}
        </div>
        <button style={styles.sheetClose} onClick={() => setSelectedProduct(null)}>✕</button>
      </div>

      {/* Вкл / Выкл */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>⚡ Статус</div>
        <button
          style={{
            width: '100%', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15,
            background: selectedProduct.item.enabled ? 'rgba(255,77,106,0.12)' : 'rgba(60,200,161,0.12)',
            color: selectedProduct.item.enabled ? AP.danger : AP.accent,
            boxShadow: selectedProduct.item.enabled ? NEU.danger : NEU.tealSm,
          }}
          disabled={saving}
          onClick={() => {
            toggleEnabled(selectedProduct.catalogId, selectedProduct.index, selectedProduct.item.enabled);
          }}
        >
          {saving ? '...' : (selectedProduct.item.enabled ? '○ Выключить товар' : '● Включить товар')}
        </button>
      </div>

      {/* Цена */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>💰 Цена</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="number"
            value={editingProduct.price}
            onChange={e => setEditingProduct(p => ({ ...p, price: e.target.value }))}
            style={{ ...styles.priceInput, flex: 1, fontSize: 16, padding: '10px 12px' }}
            min="0"
            placeholder="Цена ₽"
          />
          <button
            style={{ ...styles.btnSave, padding: '10px 18px', fontSize: 14 }}
            disabled={saving}
            onClick={() => saveItem(selectedProduct.catalogId, selectedProduct.index, { price: Number(editingProduct.price) }).then(ok => ok && showToast('Цена сохранена'))}
          >
            {saving ? '...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {/* Скидка */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>🏷 Скидка (%)</div>
        <div style={{ fontSize: 12, color: AP.muted, marginBottom: 8 }}>
          Индивидуальная скидка на этот товар. Категорийная скидка (роллы −30%, сеты −20%) применяется если скидка не задана.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="number"
            value={editingProduct.discount}
            onChange={e => setEditingProduct(p => ({ ...p, discount: e.target.value }))}
            style={{ ...styles.priceInput, flex: 1, fontSize: 16, padding: '10px 12px' }}
            min="0"
            max="99"
            placeholder="напр. 25"
          />
          <button
            style={{ ...styles.btnSave, padding: '10px 18px', fontSize: 14 }}
            disabled={saving}
            onClick={() => saveItem(selectedProduct.catalogId, selectedProduct.index, {
              discount: editingProduct.discount === '' ? null : Number(editingProduct.discount)
            }).then(ok => ok && showToast('Скидка сохранена'))}
          >
            {saving ? '...' : 'Сохранить'}
          </button>
        </div>
        {selectedProduct.item.discount != null && (
          <button
            style={{ marginTop: 8, background: 'none', border: 'none', color: AP.muted, fontSize: 12, cursor: 'pointer', padding: '4px 0' }}
            onClick={() => saveItem(selectedProduct.catalogId, selectedProduct.index, { discount: null }).then(ok => {
              if (ok) { setEditingProduct(p => ({ ...p, discount: '' })); showToast('Скидка сброшена'); }
            })}
          >
            × Сбросить индивидуальную скидку
          </button>
        )}
      </div>

      {/* Допродажи и подарки */}
      {selectedProduct.item.sku && (
        <div style={styles.sheetSection}>
          <div style={styles.sheetSectionTitle}>⭐ Роль товара</div>
          <div style={styles.giftBtnsGrid}>
            {(() => {
              const sku = String(selectedProduct.item.sku);
              const inUpsell = upsellSkus.includes(sku);
              const inPromo = promoGiftSkus.includes(sku);
              const inThreshold = thresholdGiftSkus.includes(sku);
              return (<>
                <button
                  style={inUpsell ? styles.giftBtnAdd : styles.giftBtnRemove}
                  disabled={upsellSaving === sku}
                  onClick={() => toggleUpsell(selectedProduct.item.sku, inUpsell)}
                >
                  {upsellSaving === sku ? '...' : (inUpsell ? '★ Допродажа ✓' : '☆ Допродажа')}
                </button>
                <button
                  style={(inPromo || inThreshold) ? styles.giftBtnAdd : styles.giftBtnRemove}
                  onClick={() => openGiftRuleForProduct(selectedProduct.item.sku, 'promo')}
                >
                  {inPromo ? '🎟 Промокод ✓' : '🎟 Промокод'}
                </button>
                <button
                  style={inThreshold ? styles.giftBtnAdd : styles.giftBtnRemove}
                  onClick={() => openGiftRuleForProduct(selectedProduct.item.sku, 'threshold')}
                >
                  {inThreshold ? '🎁 Порог ✓' : '🎁 Порог'}
                </button>
              </>);
            })()}
          </div>
        </div>
      )}
    </div>
  </>
)}

{/* ─── Шторка пользователя ─── */}
{selectedUser && (
  <>
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 298, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={() => setSelectedUser(null)}
    />
    <div style={styles.userSheet}>
      <div style={styles.menuHandle} />

      {/* Профиль */}
      <div style={styles.sheetHeader}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.sheetName}>{selectedUser.name || 'Без имени'}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
            <span style={styles.tariffBadge(selectedUser.tariff)}>{selectedUser.tariff}₽</span>
            {selectedUser.is_ambassador && <span style={styles.ambBadge}>AMB</span>}
            <span style={{ fontSize: 11, fontWeight: 700, color: selectedUser.subscription_status === 'активно' ? AP.accent : AP.danger }}>
              {selectedUser.subscription_status || '—'}
            </span>
            {selectedUser.balance_shc > 0 && (
              <span style={{ fontSize: 11, color: AP.yellow }}>💎 {Math.round(selectedUser.balance_shc)} SHC</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: AP.muted, marginTop: 4 }}>
            {selectedUser.phone && <span>{selectedUser.phone} · </span>}
            <span>ID {selectedUser.telegram_id}</span>
          </div>
          {selectedUser.subscription_start && (
            <div style={{ fontSize: 11, color: AP.muted, marginTop: 2 }}>
              {selectedUser.subscription_start} → {selectedUser.subscription_end}
            </div>
          )}
        </div>
        <button
          style={styles.sheetClose}
          onClick={() => setSelectedUser(null)}
        >
          ✕
        </button>
      </div>

      {/* SHC */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>💎 Начислить SHC</div>
        <div style={{ fontSize: 12, color: AP.muted, marginBottom: 8 }}>
          Текущий баланс: {Math.round(Number(selectedUser.balance_shc) || 0)} SHC
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {[5, 50].map(amount => (
            <button
              key={amount}
              type="button"
              style={{ ...styles.btnSmall, flex: 1, textAlign: 'center', color: AP.yellow }}
              onClick={() => setShcAmount(String(amount))}
              disabled={!!addingShc}
            >
              +{amount} SHC
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="number"
            min="1"
            max="1000000"
            step="1"
            placeholder="Количество SHC"
            value={shcAmount}
            onChange={e => setShcAmount(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addShc(selectedUser.telegram_id); }}
            style={{ ...styles.daysInput, flex: 1, width: 'auto', fontSize: 14 }}
            disabled={!!addingShc}
          />
          <button
            style={{ ...styles.extendBtn, padding: '10px 18px', fontSize: 13 }}
            onClick={() => addShc(selectedUser.telegram_id)}
            disabled={!!addingShc || !shcAmount}
          >
            {addingShc === selectedUser.telegram_id ? '...' : 'Начислить'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: AP.muted, marginTop: 8 }}>
          5 SHC — награда за игру, 50 SHC — базовый бонус за друга.
        </div>
      </div>

      {/* Подарки — PROMINENTLY */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>🎁 Подарки</div>
        {selectedUser.gifts && (
          <div style={{ fontSize: 13, color: AP.muted, marginBottom: 8 }}>
            Получено: {selectedUser.gifts.claimed || 0} из {selectedUser.gifts.totalWindows || 0} окон
            {selectedUser.gifts.lastClaimed ? ` · Последний: ${selectedUser.gifts.lastClaimed}` : ''}
          </div>
        )}
        <div style={styles.giftBtnsGrid}>
          <button
            style={styles.giftBtnAdd}
            onClick={() => grantGift(selectedUser.telegram_id, 'roll')}
            disabled={!!grantingGift}
          >
            {grantingGift === selectedUser.telegram_id + '+roll' ? '...' : '＋ Добавить ролл'}
          </button>
          <button
            style={styles.giftBtnRemove}
            onClick={() => claimGift(selectedUser.telegram_id, 'roll')}
            disabled={!!grantingGift}
          >
            {grantingGift === selectedUser.telegram_id + '-roll' ? '...' : '− Списать ролл'}
          </button>
          <button
            style={styles.giftBtnAdd}
            onClick={() => grantGift(selectedUser.telegram_id, 'set')}
            disabled={!!grantingGift}
          >
            {grantingGift === selectedUser.telegram_id + '+set' ? '...' : '＋ Добавить сет'}
          </button>
          <button
            style={styles.giftBtnRemove}
            onClick={() => claimGift(selectedUser.telegram_id, 'set')}
            disabled={!!grantingGift}
          >
            {grantingGift === selectedUser.telegram_id + '-set' ? '...' : '− Списать сет'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: AP.muted, marginTop: 8 }}>
          «Добавить» — выдать новый подарок. «Списать» — отметить как полученный.
        </div>
      </div>

      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>Автопродление</div>
        <div style={{ color: AP.muted, fontSize: 12, marginBottom: 10 }}>
          {selectedUser.auto_renew_disabled
            ? 'Отключено'
            : selectedUser.has_payment_method ? 'Включено' : 'Не подключено'}
          {selectedUser.subscription_end && ` · Подписка до ${selectedUser.subscription_end}`}
        </div>
        <button
          style={{ ...styles.giftBtnRemove, width: '100%', minHeight: 44 }}
          onClick={() => cancelAutoRenew(selectedUser)}
          disabled={!!cancelingAutoRenew || (!!selectedUser.auto_renew_disabled && !selectedUser.has_payment_method)}
        >
          {cancelingAutoRenew === selectedUser.telegram_id ? 'Отключение…'
            : selectedUser.auto_renew_disabled && !selectedUser.has_payment_method
              ? 'Автопродление отключено' : 'Отключить автопродление'}
        </button>
        <div style={{ color: AP.muted, fontSize: 11, marginTop: 8 }}>
          Отключает будущие списания. Оплаченный срок подписки сохраняется.
        </div>
      </div>

      {/* Тариф */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>📋 Смена тарифа</div>
        <div style={styles.tariffRow}>
          {['290', '490', '1190'].map(t => (
            <button
              key={t}
              style={subEdit?.telegram_id === selectedUser.telegram_id && subEdit?.tariff === t
                ? styles.tariffBtnActive
                : selectedUser.tariff === t
                  ? { ...styles.tariffBtnActive, boxShadow: NEU.btnIn }
                  : styles.tariffBtnInactive}
              onClick={() => setSubEdit(
                subEdit?.telegram_id === selectedUser.telegram_id && subEdit?.tariff === t
                  ? null
                  : { telegram_id: selectedUser.telegram_id, tariff: t, end_date: defaultEndDate() }
              )}
              disabled={!!settingTariff}
            >
              {settingTariff === selectedUser.telegram_id ? '...' : `${t}₽`}
            </button>
          ))}
        </div>
        {subEdit?.telegram_id === selectedUser.telegram_id && (
          <div style={styles.subEditRow}>
            <span style={{ color: AP.accent, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {subEdit.tariff}₽ до:
            </span>
            <input
              type="date"
              value={subEdit.end_date}
              onChange={e => setSubEdit(prev => ({ ...prev, end_date: e.target.value }))}
              style={styles.dateInput}
            />
            <button style={styles.applyBtn} onClick={applySubscription} disabled={!!settingTariff}>
              {settingTariff === selectedUser.telegram_id ? '...' : '✓ Применить'}
            </button>
            <button style={styles.btnCancel} onClick={() => setSubEdit(null)}>✕</button>
          </div>
        )}
      </div>

      {/* Продление */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>⏱ Продление подписки</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="number" min="1" max="365"
            placeholder="Количество дней"
            value={extendingDays[selectedUser.telegram_id] || ''}
            onChange={e => setExtendingDays(prev => ({ ...prev, [selectedUser.telegram_id]: e.target.value }))}
            style={{ ...styles.daysInput, flex: 1, width: 'auto', fontSize: 14 }}
          />
          <button
            style={{ ...styles.extendBtn, padding: '10px 18px', fontSize: 13 }}
            onClick={() => extendSub(selectedUser.telegram_id)}
            disabled={!!extending || !extendingDays[selectedUser.telegram_id]}
          >
            {extending === selectedUser.telegram_id ? '...' : 'Продлить'}
          </button>
        </div>
      </div>

      {/* Редактирование профиля */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>👤 Профиль пользователя</div>
        <button
          style={{ ...styles.btnSmall, width: '100%', textAlign: 'center', padding: '12px 14px', fontSize: 13, color: AP.text }}
          onClick={() => setEditingUser(selectedUser)}
        >
          ✎ Редактировать имя и телефон
        </button>
      </div>

      {/* Заметка */}
      <div style={styles.sheetSection}>
        <div style={styles.sheetSectionTitle}>📝 Заметка</div>
        <input
          type="text"
          placeholder="Заметка об этом пользователе..."
          defaultValue={selectedUser.notes || ''}
          key={selectedUser.telegram_id + '_sheet_notes'}
          onBlur={e => { if (e.target.value !== (selectedUser.notes || '')) saveNotes(selectedUser.telegram_id, e.target.value); }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
          style={styles.notesInput}
        />
      </div>

      {/* Рефералы */}
      {(selectedUser.referrals_count > 0 || selectedUser.invited_by) && (
        <div style={styles.sheetSection}>
          <div style={styles.sheetSectionTitle}>📈 Рефералы</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12 }}>
            {selectedUser.referrals_count > 0 && <span style={{ color: AP.accent }}>👥 {selectedUser.referrals_count} рефералов</span>}
            {Number(selectedUser.shc_earned) > 0 && <span style={{ color: AP.yellow }}>💎 {Math.round(selectedUser.shc_earned)} SHC заработано</span>}
            {selectedUser.invited_by && <span style={{ color: AP.muted }}>↑ Пригласил: {selectedUser.invited_by_name || selectedUser.invited_by}</span>}
          </div>
        </div>
      )}

      {/* Опасная зона */}
      <div style={{ ...styles.sheetSection, marginBottom: 40 }}>
        <div style={{ ...styles.sheetSectionTitle, color: AP.danger }}>⚠️ Опасная зона</div>
        <button
          style={{ ...styles.resetSubBtn, width: '100%', textAlign: 'center' }}
          onClick={() => resetSubscription(selectedUser.telegram_id, selectedUser.name)}
          disabled={!!resettingSub}
        >
          {resettingSub === selectedUser.telegram_id ? '...' : '🗑 Сбросить подписку'}
        </button>
        <div style={{ fontSize: 11, color: AP.muted, marginTop: 6 }}>
          Пользователь увидит экран оплаты при следующем входе.
        </div>
      </div>
    </div>
  </>
)}

      {/* ─── Toast уведомления ─── */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 300, display: 'flex', flexDirection: 'column', gap: 8,
          alignItems: 'center', pointerEvents: 'none',
        }}>
          {toasts.map(t => (
            <div key={t.id} style={{
              padding: '10px 20px',
              borderRadius: 12,
              background: t.type === 'error' ? AP.danger : AP.accent,
              color: t.type === 'error' ? '#fff' : '#051a12',
              fontWeight: 700,
              fontSize: 13,
              boxShadow: t.type === 'error' ? NEU.danger : NEU.teal,
              whiteSpace: 'nowrap',
              fontFamily: '"Montserrat", sans-serif',
            }}>
              {t.text}
            </div>
          ))}
        </div>
      )}

      {editingUser && (
        <EditProfileModal
          mode="admin"
          currentUser={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={async () => { await loadSubscribers(); }}
        />
      )}
    </div>
  );
}

// ─── Palette ───────────────────────────────────────────────────
const AP = {
  bg:      '#1a1a1a',
  surface: '#202024',
  raise:   '#2c2c30',
  shadow:  '#111113',
  border:  'rgba(255,255,255,0.07)',
  accent:  '#3CC8A1',
  accentD: '#28a882',
  accentL: '#50dcb0',
  text:    '#e8e8f0',
  muted:   '#888899',
  danger:  '#ff4d6a',
  warn:    '#f5923a',
  yellow:  '#ffd600',
};

// JSX backward-compat (CP.cyan / CP.pink / CP.green / CP.muted / CP.text / CP.yellow)
const CP = {
  bg: AP.bg, surface: AP.surface, card: AP.surface, border: AP.border,
  text: AP.text, muted: AP.muted, yellow: AP.yellow,
  cyan: AP.accent, cyanDim: AP.accentD, green: AP.accent, pink: AP.danger,
};

// ─── Neumorphic shadow tokens ──────────────────────────────────
const NEU = {
  card:   '6px 6px 16px #111113, -4px -4px 12px #2c2c30',
  btnOut: '3px 3px 8px #111113, -2px -2px 6px #2e2e36',
  btnIn:  'inset 2px 2px 6px #111113, inset -1px -1px 4px #2e2e36',
  teal:   '0 4px 16px rgba(60,200,161,0.38), 0 2px 8px rgba(60,200,161,0.22)',
  tealSm: '0 2px 10px rgba(60,200,161,0.26)',
  danger: '0 3px 12px rgba(255,77,106,0.28)',
  warn:   '0 3px 12px rgba(245,146,58,0.28)',
};

const styles = {
  container: {
    maxWidth: 480,
    margin: '0 auto',
    padding: '12px 14px 60px',
    fontFamily: '"Montserrat", "Segoe UI", Arial, sans-serif',
    color: AP.text,
    background: AP.bg,
    minHeight: '100vh',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  loginCard: {
    maxWidth: 320,
    margin: '100px auto',
    padding: 28,
    background: AP.surface,
    borderRadius: 20,
    textAlign: 'center',
    boxShadow: NEU.card,
  },
  title: {
    color: AP.accent,
    margin: '0 0 14px',
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '0.06em',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: AP.bg,
    border: `1px solid ${AP.border}`,
    borderRadius: 10,
    color: AP.text,
    fontSize: 14,
    boxSizing: 'border-box',
    marginBottom: 10,
    outline: 'none',
    boxShadow: NEU.btnIn,
  },
  btnPrimary: {
    width: '100%',
    padding: '12px 20px',
    background: AP.accent,
    color: '#051a12',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: NEU.teal,
    transition: 'all 0.2s',
  },
  btnSmall: {
    padding: '6px 14px',
    background: AP.surface,
    color: AP.muted,
    border: 'none',
    borderRadius: 8,
    fontSize: 11,
    cursor: 'pointer',
    boxShadow: NEU.btnOut,
    fontWeight: 600,
    transition: 'all 0.2s',
  },
  dashboardHero: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    marginBottom: 12,
    background: AP.surface,
    borderRadius: 16,
    boxShadow: NEU.card,
  },
  dashboardTitle: {
    color: AP.text,
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 5,
  },
  dashboardText: {
    color: AP.muted,
    fontSize: 12,
    lineHeight: 1.45,
  },
  dashboardStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginBottom: 12,
  },
  dashboardStat: {
    padding: 10,
    background: AP.surface,
    borderRadius: 12,
    boxShadow: NEU.btnOut,
  },
  dashboardStatValue: {
    display: 'block',
    color: AP.accent,
    fontSize: 18,
    fontWeight: 800,
  },
  dashboardStatLabel: {
    display: 'block',
    color: AP.muted,
    fontSize: 10,
    marginTop: 2,
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  dashboardButton: {
    minHeight: 78,
    padding: 12,
    background: AP.surface,
    color: AP.text,
    border: 'none',
    borderRadius: 14,
    textAlign: 'left',
    cursor: 'pointer',
    boxShadow: NEU.btnOut,
  },
  dashboardButtonTitle: {
    display: 'block',
    color: AP.text,
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 4,
  },
  dashboardButtonText: {
    display: 'block',
    color: AP.muted,
    fontSize: 10,
    lineHeight: 1.35,
  },
  giftRuleForm: {
    padding: 12,
    background: AP.surface,
    borderRadius: 14,
    boxShadow: NEU.card,
    marginBottom: 14,
  },
  giftRuleFormTitle: {
    color: AP.text,
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 10,
  },
  giftRuleGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 8,
  },
  giftRuleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  giftRuleCard: {
    padding: 12,
    background: AP.surface,
    borderRadius: 14,
    boxShadow: NEU.btnOut,
  },
  giftRuleCardOff: {
    padding: 12,
    background: AP.surface,
    borderRadius: 14,
    boxShadow: NEU.btnOut,
    opacity: 0.58,
  },
  giftRuleHead: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 10,
  },
  giftRuleName: {
    color: AP.text,
    fontSize: 13,
    fontWeight: 800,
  },
  giftRuleMeta: {
    color: AP.muted,
    fontSize: 11,
    marginTop: 3,
    lineHeight: 1.35,
  },
  giftRuleBadge: {
    color: AP.accent,
    fontSize: 10,
    fontWeight: 800,
    padding: '3px 7px',
    borderRadius: 6,
    background: 'rgba(60,200,161,0.12)',
  },
  giftRuleBadgeOff: {
    color: AP.muted,
    fontSize: 10,
    fontWeight: 800,
    padding: '3px 7px',
    borderRadius: 6,
    background: 'rgba(255,255,255,0.06)',
  },
  giftRuleActions: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  error: { color: AP.danger, marginTop: 10, fontSize: 12 },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    paddingBottom: 12,
    borderBottom: `1px solid ${AP.border}`,
  },
  // ─── Flat nav tabs ────────────────────────────────────────────
  navRow: {
    display: 'flex',
    gap: 6,
  },
  navTab: {
    flex: 1,
    padding: '11px 4px',
    background: AP.surface,
    color: AP.muted,
    border: 'none',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: NEU.btnOut,
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
    minHeight: 44,
  },
  navTabActive: {
    flex: 1,
    padding: '11px 4px',
    background: 'rgba(60,200,161,0.1)',
    color: AP.accent,
    border: 'none',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `${NEU.btnIn}, ${NEU.tealSm}`,
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
    minHeight: 44,
  },
  // ─── Dropdown "Ещё" ──────────────────────────────────────────
  moreMenu: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    background: AP.raise,
    borderRadius: 16,
    padding: '8px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3)',
    zIndex: 99,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 220,
  },
  moreItem: {
    padding: '11px 14px',
    background: 'transparent',
    color: AP.text,
    border: 'none',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
    minHeight: 44,
  },
  moreItemActive: {
    padding: '11px 14px',
    background: 'rgba(60,200,161,0.1)',
    color: AP.accent,
    border: 'none',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'left',
    minHeight: 44,
  },
  // ─── Card internals ──────────────────────────────────────────
  cardDivider: {
    height: 1,
    background: AP.border,
    margin: '2px 0',
  },
  actionsToggle: {
    background: 'transparent',
    border: 'none',
    color: AP.muted,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 0',
    letterSpacing: '0.04em',
    minHeight: 32,
  },
  badge: {
    padding: '2px 7px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    background: AP.surface,
    color: AP.muted,
    boxShadow: NEU.btnOut,
  },
  // ─── Catalog sub-tabs ────────────────────────────────────────
  catalogTabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    marginBottom: 10,
  },
  catBtn: {
    padding: '4px 9px',
    background: AP.surface,
    color: AP.muted,
    border: 'none',
    borderRadius: 6,
    fontSize: 10,
    cursor: 'pointer',
    boxShadow: NEU.btnOut,
    fontWeight: 600,
    transition: 'all 0.15s',
  },
  catBtnActive: {
    padding: '4px 9px',
    background: 'rgba(60,200,161,0.1)',
    color: AP.accent,
    border: 'none',
    borderRadius: 6,
    fontSize: 10,
    cursor: 'pointer',
    fontWeight: 700,
    boxShadow: `${NEU.btnIn}, ${NEU.tealSm}`,
    transition: 'all 0.15s',
  },
  muted: { color: AP.muted, fontSize: 12 },
  productList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
  },
  productRow: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px',
    background: AP.surface,
    borderRadius: 10,
    gap: 6,
    boxShadow: NEU.card,
  },
  productInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    minWidth: 0,
  },
  productName: { fontSize: 11, color: AP.text, wordBreak: 'break-word', fontWeight: 500 },
  sku: { fontSize: 10, color: AP.muted },
  productActions: { display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'space-between' },
  price: {
    fontSize: 12,
    fontWeight: 700,
    color: AP.accent,
    cursor: 'pointer',
  },
  editRow: { display: 'flex', gap: 4, alignItems: 'center' },
  priceInput: {
    width: 56,
    padding: '4px 5px',
    background: AP.bg,
    border: `1px solid ${AP.border}`,
    borderRadius: 6,
    color: AP.text,
    fontSize: 12,
    textAlign: 'right',
    boxShadow: NEU.btnIn,
    outline: 'none',
  },
  btnSave: {
    padding: '4px 9px',
    background: AP.accent,
    color: '#051a12',
    border: 'none',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: NEU.tealSm,
  },
  btnCancel: {
    padding: '4px 8px',
    background: AP.surface,
    color: AP.muted,
    border: 'none',
    borderRadius: 6,
    fontSize: 11,
    cursor: 'pointer',
    boxShadow: NEU.btnOut,
  },
  toggleOn: {
    padding: '3px 7px',
    background: 'rgba(60,200,161,0.12)',
    color: AP.accent,
    border: 'none',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    cursor: 'pointer',
    minWidth: 34,
    boxShadow: NEU.tealSm,
  },
  toggleOff: {
    padding: '3px 7px',
    background: 'rgba(255,77,106,0.1)',
    color: AP.danger,
    border: 'none',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    cursor: 'pointer',
    minWidth: 34,
    boxShadow: NEU.danger,
  },
  upsellOn: {
    width: '100%',
    padding: '4px 8px',
    fontSize: 10,
    fontWeight: 700,
    border: `1px solid ${AP.accent}`,
    borderRadius: 4,
    cursor: 'pointer',
    backgroundColor: AP.accent,
    color: '#000',
  },
  upsellOff: {
    width: '100%',
    padding: '4px 8px',
    fontSize: 10,
    fontWeight: 700,
    border: '1px solid #333',
    borderRadius: 4,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: AP.muted,
  },
  // Subscribers
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    marginBottom: 10,
  },
  statBadge: {
    padding: '3px 10px',
    background: AP.surface,
    borderRadius: 6,
    fontSize: 11,
    color: AP.muted,
    boxShadow: NEU.btnOut,
    fontWeight: 600,
  },
  filterRow: {
    display: 'flex',
    gap: 6,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  select: {
    padding: '6px 10px',
    background: AP.surface,
    border: `1px solid ${AP.border}`,
    borderRadius: 8,
    color: AP.text,
    fontSize: 12,
    boxShadow: NEU.btnIn,
    outline: 'none',
  },
  searchInput: {
    flex: 1,
    minWidth: 130,
    padding: '6px 10px',
    background: AP.surface,
    border: `1px solid ${AP.border}`,
    borderRadius: 8,
    color: AP.text,
    fontSize: 12,
    outline: 'none',
    boxShadow: NEU.btnIn,
  },
  subsCount: { fontSize: 11, color: AP.muted, marginBottom: 6 },
  subsList: { display: 'flex', flexDirection: 'column', gap: 8 },
  subRow: {
    padding: '12px',
    background: AP.surface,
    borderRadius: 14,
    boxShadow: NEU.card,
  },
  subCard: {
    padding: '12px',
    background: AP.surface,
    borderRadius: 14,
    boxShadow: NEU.card,
  },
  subMain: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  subName: { fontSize: 13, color: AP.text, fontWeight: 700 },
  tariffBadge: (tariff) => ({
    padding: '2px 7px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    background: tariff === '9990' ? 'rgba(255,214,0,0.15)' : tariff === '1190' ? 'rgba(60,200,161,0.14)' : tariff === '490' ? 'rgba(60,200,161,0.1)' : 'rgba(130,130,150,0.15)',
    color: tariff === '9990' ? AP.yellow : tariff === '1190' ? AP.accent : tariff === '490' ? AP.accentL : AP.muted,
    boxShadow: tariff === '9990' ? '0 1px 6px rgba(255,214,0,0.25)' : (tariff === '1190' || tariff === '490') ? NEU.tealSm : 'none',
  }),
  ambBadge: {
    padding: '2px 6px',
    borderRadius: 5,
    fontSize: 9,
    fontWeight: 700,
    background: 'rgba(255,214,0,0.15)',
    color: AP.yellow,
    boxShadow: '0 1px 6px rgba(255,214,0,0.2)',
  },
  subDetails: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 7,
    fontSize: 11,
    color: AP.muted,
    wordBreak: 'break-all',
  },
  refRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    fontSize: 11,
    color: AP.accent,
    marginTop: 4,
  },
  // Gifts
  giftsRow: {
    marginTop: 8,
    paddingTop: 8,
    borderTop: `1px solid ${AP.border}`,
  },
  giftsSummary: {
    fontSize: 11,
    color: AP.accent,
    marginRight: 7,
  },
  giftsLast: {
    fontSize: 11,
    color: AP.muted,
  },
  giftsWindows: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  giftClaimed: {
    padding: '2px 6px',
    background: 'rgba(60,200,161,0.1)',
    borderRadius: 5,
    fontSize: 10,
    color: AP.accent,
    boxShadow: NEU.tealSm,
  },
  grantRow: {
    display: 'flex',
    gap: 5,
    marginBottom: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  grantInput: {
    flex: 1,
    padding: '7px 10px',
    background: AP.surface,
    border: `1px solid ${AP.border}`,
    borderRadius: 8,
    color: AP.text,
    fontSize: 12,
    outline: 'none',
    boxShadow: NEU.btnIn,
  },
  grantBtn: {
    padding: '10px 14px',
    background: 'rgba(60,200,161,0.1)',
    color: AP.accent,
    border: 'none',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: NEU.tealSm,
    minHeight: 44,
  },
  claimBtn: {
    padding: '10px 14px',
    background: 'rgba(255,77,106,0.1)',
    color: AP.danger,
    border: 'none',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: NEU.danger,
    minHeight: 44,
  },
  resetSubBtn: {
    padding: '10px 14px',
    background: 'rgba(255,77,106,0.1)',
    color: AP.danger,
    border: 'none',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: NEU.danger,
    minHeight: 44,
  },
  tariffRow: {
    display: 'flex',
    gap: 5,
    marginTop: 6,
    flexWrap: 'wrap',
  },
  tariffBtnActive: {
    padding: '8px 14px',
    background: 'rgba(60,200,161,0.12)',
    color: AP.accent,
    border: 'none',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `${NEU.btnIn}, ${NEU.tealSm}`,
    minHeight: 40,
  },
  tariffBtnInactive: {
    padding: '8px 14px',
    background: AP.surface,
    color: AP.muted,
    border: 'none',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: NEU.btnOut,
    minHeight: 40,
  },
  daysInput: {
    width: 52,
    padding: '8px 6px',
    background: AP.bg,
    color: AP.text,
    border: `1px solid ${AP.border}`,
    borderRadius: 7,
    fontSize: 11,
    textAlign: 'center',
    boxShadow: NEU.btnIn,
    outline: 'none',
  },
  extendBtn: {
    padding: '8px 12px',
    background: 'rgba(245,146,58,0.12)',
    color: AP.warn,
    border: 'none',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: NEU.warn,
    minHeight: 40,
  },
  notesRow: {
    marginTop: 8,
  },
  notesInput: {
    width: '100%',
    padding: '6px 10px',
    background: AP.bg,
    color: AP.text,
    border: `1px solid ${AP.border}`,
    borderRadius: 8,
    fontSize: 11,
    boxSizing: 'border-box',
    boxShadow: NEU.btnIn,
    outline: 'none',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  statCard: {
    background: AP.surface,
    borderRadius: 14,
    padding: '14px 16px',
    boxShadow: NEU.card,
  },
  statVal: {
    fontSize: 26,
    fontWeight: 700,
    color: AP.accent,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: 11,
    color: AP.muted,
    marginTop: 4,
    letterSpacing: '0.04em',
  },
  giftStatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    padding: '9px 0',
    borderTop: `1px solid ${AP.border}`,
  },
  giftStatTitle: {
    color: AP.text,
    fontSize: 13,
    fontWeight: 700,
  },
  giftStatText: {
    color: AP.muted,
    fontSize: 11,
    lineHeight: 1.45,
    marginTop: 3,
  },
  giftStatCount: {
    color: AP.accent,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1,
  },
  // Banners
  bannerSlot: {
    background: AP.surface,
    borderRadius: 14,
    padding: 12,
    boxShadow: NEU.card,
  },
  bannerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  bannerPreview: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  bannerImg: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: 8,
  },
  bannerEmpty: {
    width: '100%',
    aspectRatio: '8/3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    background: AP.bg,
    color: AP.muted,
    fontSize: 12,
    border: `1px dashed ${AP.border}`,
  },
  // Pricing
  priceField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    flex: 1,
    minWidth: 70,
  },
  priceLabel: {
    fontSize: 10,
    color: AP.muted,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  priceInput2: {
    padding: '8px 7px',
    background: AP.bg,
    border: `1px solid ${AP.border}`,
    borderRadius: 8,
    color: AP.accent,
    fontSize: 14,
    fontWeight: 700,
    textAlign: 'center',
    width: '100%',
    boxSizing: 'border-box',
    boxShadow: NEU.btnIn,
    outline: 'none',
  },
  // Add User
  addCard: {
    background: AP.surface,
    borderRadius: 16,
    padding: 16,
    boxShadow: NEU.card,
  },
  addTitle: {
    margin: '0 0 14px',
    fontSize: 13,
    fontWeight: 700,
    color: AP.accent,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  fieldLabel: {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    color: AP.muted,
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  subEditRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    padding: '8px 10px',
    background: 'rgba(60,200,161,0.06)',
    borderRadius: 10,
    flexWrap: 'wrap',
    boxShadow: 'inset 0 0 0 1px rgba(60,200,161,0.2)',
  },
  dateInput: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    padding: '5px 8px',
    background: AP.bg,
    border: `1px solid ${AP.border}`,
    borderRadius: 7,
    color: AP.text,
    fontSize: 12,
    colorScheme: 'dark',
    WebkitAppearance: 'none',
    appearance: 'none',
    boxSizing: 'border-box',
    boxShadow: NEU.btnIn,
    outline: 'none',
  },
  applyBtn: {
    padding: '5px 14px',
    background: AP.accent,
    color: '#051a12',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: NEU.tealSm,
  },
  backBtn: {
    minWidth: 86,
    height: 44,
    background: AP.surface,
    color: AP.text,
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    padding: '0 12px',
    boxShadow: NEU.btnOut,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
  },
  headerSpacer: {
    width: 86,
    height: 44,
    flexShrink: 0,
  },
  currentTabLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: AP.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    textAlign: 'center',
  },
  menuHandle: {
    width: 36,
    height: 4,
    background: AP.border,
    borderRadius: 2,
    margin: '0 auto 14px',
  },
  menuSheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 199,
    background: AP.surface,
    borderRadius: '20px 20px 0 0',
    padding: '8px 12px 32px',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.6)',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '13px 14px',
    background: 'transparent',
    border: 'none',
    borderRadius: 14,
    cursor: 'pointer',
    textAlign: 'left',
    minHeight: 54,
  },
  menuItemActive: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '13px 14px',
    background: 'rgba(60,200,161,0.1)',
    border: 'none',
    borderRadius: 14,
    cursor: 'pointer',
    textAlign: 'left',
    minHeight: 54,
  },
  // ─── 2-column user card grid ──────────────────────────────────
  userCardGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 10,
  },
  userCard: {
    background: AP.surface,
    border: 'none',
    borderRadius: 14,
    padding: '12px 10px',
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: NEU.card,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minHeight: 110,
    transition: 'transform 0.1s',
  },
  userCardAvatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'rgba(60,200,161,0.15)',
    color: AP.accent,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
  },
  userCardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  userCardName: {
    fontSize: 13,
    fontWeight: 700,
    color: AP.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userCardMeta: {
    display: 'flex',
    gap: 5,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  userCardPhone: {
    fontSize: 10,
    color: AP.muted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userCardDate: {
    fontSize: 10,
    color: AP.muted,
  },
  // ─── User detail sheet ────────────────────────────────────────
  userSheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 299,
    background: AP.bg,
    borderRadius: '20px 20px 0 0',
    padding: '8px 16px 40px',
    maxHeight: '92vh',
    overflowY: 'auto',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.7)',
  },
  sheetHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: `1px solid ${AP.border}`,
    gap: 10,
  },
  sheetName: {
    fontSize: 20,
    fontWeight: 700,
    color: AP.text,
  },
  sheetClose: {
    width: 34,
    height: 34,
    background: AP.surface,
    border: 'none',
    borderRadius: 10,
    color: AP.muted,
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: NEU.btnOut,
  },
  sheetSection: {
    background: AP.surface,
    borderRadius: 14,
    padding: '14px 14px',
    marginBottom: 10,
    boxShadow: NEU.card,
  },
  sheetSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: AP.muted,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  giftBtnsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 4,
  },
  giftBtnAdd: {
    padding: '14px 8px',
    background: 'rgba(60,200,161,0.12)',
    color: AP.accent,
    border: '1px solid rgba(60,200,161,0.3)',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: 52,
    boxShadow: NEU.tealSm,
    lineHeight: 1.3,
  },
  giftBtnRemove: {
    padding: '14px 8px',
    background: 'rgba(255,77,106,0.1)',
    color: AP.danger,
    border: '1px solid rgba(255,77,106,0.25)',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: 52,
    boxShadow: NEU.danger,
    lineHeight: 1.3,
  },
};

export default AdminPage;
